<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { onBeforeRouteLeave } from "vue-router";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { useQuoteStore } from "@/stores/QuoteStore";
import QuoteAcceptAction from "@/components/QuoteAcceptAction.vue";
import { acceptCurrentQuote } from "@/views/Quote/quoteAcceptance";
import QuoteResources from "@/components/QuoteResources.vue";
import QuoteResourceTimeline from "@/components/QuoteResourceTimeline.vue";
import QuoteEventBasics from "@/components/QuoteEventBasics.vue";
import QuoteEventRisk from "@/components/QuoteEventRisk.vue";
import QuotePtsEmailExtraction from "@/components/QuotePtsEmailExtraction.vue";
import QuotePtsRisk from "@/components/QuotePtsRisk.vue";
import QuotePtsRouteMap from "@/components/QuotePtsRouteMap.vue";
import QuoteExtras from "@/components/QuoteExtras.vue";
import QuoteFilesPanel from "@/components/QuoteFilesPanel.vue";
import CustomerSearch from "@/components/CustomerSearch.vue";
import ContactSearch from "@/components/ContactSearch.vue";
import SearchableInput from "@/components/SearchableInput.vue";
import TicketInline from "@/components/TicketInline.vue";
import apiClient from "@/apiClient";
import QuoteService from "@/services/QuoteService";
import useDebouncedFunction from "@/composables/useDebouncedFuncion";
import { useAuthStore } from "@/stores/AuthStore";
import { usePresentationMode } from "@/composables/usePresentationMode";
import {
  canRecalculateSummary as canRecalculateQuoteSummary,
  getSummaryPrerequisiteMessage,
  hasUnsavedQuoteChanges,
} from "@/views/Quote/quoteSummaryGuards";
import {
  blockingIssues,
  blockingIssuesBeforeStep,
  issuesForStep,
  quoteWorkflowIssues,
} from "@/views/Quote/quoteWorkflow";
import * as Icon from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const { maskPoundAmount } = usePresentationMode();
const breadcrumbStore = useBreadcrumbStore();
const quote = useQuoteStore();
const canManageQuote = computed(() =>
  auth.hasAnyRole(["customer-service", "super"]),
);
const canAccessAdvancedPricing = computed(() => auth.hasRole("super"));
const editable = computed(() => canManageQuote.value && !quote.data?.is_locked);
const canEditAdvancedPricing = computed(
  () => editable.value && canAccessAdvancedPricing.value,
);
const saving = ref(false);
const saveError = ref(null);
const lastSavedAt = ref(null);
const pricing = ref(null);
const pricingError = ref(null);
const recalculatingSummary = ref(false);
const sending = ref(false);
const sendError = ref(null);
const accepting = ref(false);
const acceptError = ref(null);
const unlocking = ref(false);
const unlockError = ref(null);
const duplicating = ref(false);
const duplicateError = ref(null);
const toast = reactive({
  visible: false,
  message: "",
  tone: "success",
});
const navigationError = ref("");
const lastSavedSignature = ref("");
const previewLoading = ref(false);
const previewError = ref(null);
const previewModalOpen = ref(false);
const previewFrameLoading = ref(false);
const previewFrameSrc = ref("");
const sendMessage = ref("");
const sendMessageEdited = ref(false);
const aiMessageLoading = ref(false);
const aiMessageError = ref(null);
const aiMessageLoadedFor = ref(null);
const sendConsent = ref(false);
const ticketContextOpen = ref(true);
const customerTickets = ref([]);
const ticketsLoading = ref(false);
const ticketsError = ref(null);
const selectedTicketIds = ref([]);
const ticketSelectionReady = ref(false);
const referenceTicketId = ref(null);
const lineQuantityOverrides = ref({});
const advancedPricingOpen = ref(false);
const comparisonReferenceSearchText = ref("");
let toastTimeout = null;
const isLocked = computed(() => quote.data?.is_locked ?? false);
const canUnlockQuote = computed(
  () =>
    canManageQuote.value &&
    Boolean(quote.data?.id) &&
    Boolean(quote.data?.accepted_at) &&
    isLocked.value &&
    !unlocking.value,
);
const canDuplicateQuote = computed(
  () => canManageQuote.value && Boolean(quote.data?.id) && !duplicating.value,
);
const referenceTypeShortname = computed(() =>
  String(quote.data?.reference_type?.shortname ?? "")
    .trim()
    .toLowerCase(),
);
const referenceTypeName = computed(() =>
  String(quote.data?.reference_type?.name ?? "")
    .trim()
    .toLowerCase(),
);
const isEvent = () =>
  ["event", "events"].includes(referenceTypeShortname.value) ||
  ["event", "events"].includes(referenceTypeName.value);
const isPts = () =>
  [
    "pts",
    "patient transportation",
    "patient transport",
    "patient transfer",
  ].includes(referenceTypeShortname.value) ||
  [
    "pts",
    "patient transportation",
    "patient transport",
    "patient transfer",
  ].includes(referenceTypeName.value);
const riskChatRef = ref(null);
const activeTab = ref("quote");
const ticketId = computed(() => {
  const raw = route.query.ticket_id;
  if (!raw) return null;
  const id = Number(raw);
  return Number.isNaN(id) ? null : id;
});
const canViewFinancials = computed(() => auth.hasFinanceRole());
const canDeleteQuote = computed(() => auth.hasRole("customer-service"));
const hasPricingSummary = computed(
  () => pricing.value !== null && !pricingError.value,
);
const customerId = computed(
  () => quote.data?.customer?.id ?? quote.data?.customer_id ?? null,
);
const linkedTicketId = computed(() => {
  const raw = quote.data?.ticket_id ?? ticketId.value;
  if (!raw) return null;
  const id = Number(raw);
  return Number.isNaN(id) ? null : id;
});
const canSendQuote = computed(() => {
  if (isLocked.value) return false;
  if (!quote.data?.sending_enabled) return false;
  if (!hasPricingSummary.value) return false;
  if (workflowBlockingIssues.value.length) return false;
  if (aiMessageLoading.value) return false;
  if (!sendConsent.value) return false;
  const recipientContactId = Number(
    quote.data?.recipient_contact_id ?? quote.data?.recipient_contact?.id,
  );
  return !Number.isNaN(recipientContactId) && recipientContactId > 0;
});
const canAcceptQuote = computed(
  () =>
    canManageQuote.value &&
    Boolean(quote.data?.id) &&
    isReady.value &&
    !isLocked.value &&
    !saving.value &&
    !recalculatingSummary.value &&
    !unlocking.value &&
    !sending.value &&
    !accepting.value,
);
const hasSummaryDatesConfigured = computed(() =>
  Array.isArray(quote.data?.versions?.[0]?.dates) &&
  quote.data.versions[0].dates.some(
    (entry) => String(entry?.date ?? "").trim() !== "",
  ),
);
const hasUnsavedSummaryChanges = computed(() =>
  hasUnsavedQuoteChanges({
    isReady: isReady.value,
    currentSignature: getSaveSignature(getSavePayload()),
    lastSavedSignature: lastSavedSignature.value,
  }),
);
const summaryBusy = computed(
  () => saving.value || recalculatingSummary.value,
);
const summaryPrerequisiteMessage = computed(() => {
  return getSummaryPrerequisiteMessage({
    isLocked: isLocked.value,
    hasSummaryDatesConfigured: hasSummaryDatesConfigured.value,
    hasUnsavedChanges: hasUnsavedSummaryChanges.value,
    canManageQuote: canManageQuote.value,
  });
});
const canRunSummaryRecalculation = computed(() =>
  canRecalculateQuoteSummary({
    isBusy: summaryBusy.value,
    isLocked: isLocked.value,
    hasSummaryDatesConfigured: hasSummaryDatesConfigured.value,
    hasUnsavedChanges: hasUnsavedSummaryChanges.value,
    canManageQuote: canManageQuote.value,
  }),
);
const sendDisabledMessage = computed(() => {
  if (isLocked.value) {
    return "Unlock this accepted quote before making further changes.";
  }

  if (!quote.data?.sending_enabled) {
    return "Quote sending is temporarily disabled while workflow changes are under review.";
  }

  if (!hasPricingSummary.value) {
    return pricingError.value
      ? pricingError.value
      : "Complete the quote details so a summary can be calculated before sending.";
  }

  if (workflowBlockingIssues.value.length) {
    return workflowBlockingIssues.value[0].message;
  }

  if (aiMessageLoading.value) {
    return "Drafting the message to accompany this quote.";
  }

  const recipientContactId = Number(
    quote.data?.recipient_contact_id ?? quote.data?.recipient_contact?.id,
  );
  if (Number.isNaN(recipientContactId) || recipientContactId <= 0) {
    return "Select a recipient contact before sending this quote.";
  }

  if (!sendConsent.value) {
    return "Confirm that this quote PDF should be sent to the customer.";
  }

  return "This quote is ready to send.";
});
const hasTicketTab = computed(() => !!linkedTicketId.value);
const ticketSelectionKey = computed(() => {
  const quoteId = quote.data?.id ?? route.params.id;
  if (!quoteId) return null;
  return `quote:${quoteId}:ticket-selection`;
});
const sortedTickets = computed(() => {
  const tickets = Array.isArray(customerTickets.value)
    ? customerTickets.value.slice()
    : [];
  const linkedId = Number(linkedTicketId.value);
  if (!Number.isNaN(linkedId) && linkedId > 0) {
    const alreadyListed = tickets.some(
      (ticket) => Number(ticket?.id) === linkedId,
    );
    if (!alreadyListed) {
      tickets.push({
        id: linkedId,
        subject: `Ticket #${linkedId}`,
        updated_at: null,
        created_at: null,
      });
    }
  }

  return tickets
    .sort(
      (a, b) =>
        new Date(b.updated_at ?? b.created_at ?? 0) -
        new Date(a.updated_at ?? a.created_at ?? 0),
    );
});
const selectedTicketCount = computed(() => selectedTicketIds.value.length);
const referenceTicketSummary = computed(() => {
  const id = Number(referenceTicketId.value);
  if (Number.isNaN(id) || id <= 0) return null;

  return (
    sortedTickets.value.find((ticket) => Number(ticket?.id) === id) ?? {
      id,
      subject: `Ticket #${id}`,
      updated_at: null,
      created_at: null,
    }
  );
});
const quoteWorkspaceGridClasses = computed(() =>
  referenceTicketSummary.value
    ? "xl:grid-cols-[minmax(0,2.15fr)_minmax(280px,0.72fr)_minmax(360px,1.13fr)]"
    : "xl:grid-cols-[minmax(0,2.15fr)_minmax(280px,0.78fr)]",
);
const stepIndex = ref(0);
const steps = computed(() => {
  if (isEvent()) {
    const eventSteps = [
      { id: "event-basics", label: "Essentials" },
      { id: "event-risk", label: "Risk Assessment" },
      { id: "resources", label: "Cover Plan" },
    ];
    if (canViewFinancials.value) {
      eventSteps.push({ id: "review", label: "Review & Send" });
    }
    return eventSteps;
  }
  if (isPts()) {
    const ptsSteps = [
      { id: "pts-basics", label: "Customer & Transport" },
      { id: "resources", label: "Resources" },
    ];
    if (canViewFinancials.value) {
      ptsSteps.push({ id: "review", label: "Review & Send" });
    }
    return ptsSteps;
  }
  const defaultSteps = [
    { id: "details", label: "Details" },
    { id: "resources", label: "Resources" },
  ];
  if (canViewFinancials.value) {
    defaultSteps.push({ id: "review", label: "Review & Send" });
  }
  return defaultSteps;
});
const workflowType = computed(() => (isEvent() ? "event" : isPts() ? "pts" : "default"));
const workflowIssues = computed(() =>
  quoteWorkflowIssues(quote.data, {
    type: workflowType.value,
    pricing: pricing.value,
    pricingError: pricingError.value,
  }),
);
const workflowBlockingIssues = computed(() => blockingIssues(workflowIssues.value));
const currentStepIssues = computed(() =>
  issuesForStep(workflowIssues.value, currentStep.value?.id),
);
const stepIssueCount = (stepId) =>
  blockingIssues(issuesForStep(workflowIssues.value, stepId)).length;
const displayName = computed(() => {
  const ptsName = quote.data?.versions?.[0]?.pts_risk?.patient_name;
  return (
    quote.data?.reference?.name ||
    quote.data?.display_name ||
    quote.data?.description ||
    ptsName ||
    `Quote #${quote.data?.id ?? ""}`
  );
});
const referenceLabel = computed(
  () => quote.data?.reference?.txt || `Quote #${quote.data?.id ?? ""}`,
);
const lockedQuoteMessage = computed(() => {
  if (!isLocked.value) return "";

  return "This accepted quote is locked. Unlock it to make changes, and the unlock will be recorded in the reference history.";
});

onMounted(() => {
  quote.configure(route.params.id);
  breadcrumbStore.setValue("");
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", handleBeforeUnload);
  }
});

const isReady = ref(false);
watch(
  () => quote.isLoading,
  async (loading) => {
    if (loading) {
      isReady.value = false;
      return;
    }

    await nextTick();
    lastSavedSignature.value = getSaveSignature(getSavePayload());
    isReady.value = true;
  },
  { immediate: true },
);

watch(
  ticketId,
  (id) => {
    if (!id && activeTab.value === "ticket") {
      activeTab.value = "quote";
    }
  },
  { immediate: true },
);

watch(
  customerId,
  async () => {
    customerTickets.value = [];
    ticketsError.value = null;
    ticketsLoading.value = false;
    selectedTicketIds.value = normalizeTicketIds([linkedTicketId.value]);
    ticketSelectionReady.value = false;
    if (!customerId.value) {
      if (selectedTicketIds.value.length) {
        persistTicketSelection(selectedTicketIds.value);
      }
      ticketSelectionReady.value = true;
      return;
    }

    await loadCustomerTickets();
    const restored = restoreTicketSelection();
    if (!restored && linkedTicketId.value) {
      selectedTicketIds.value = normalizeTicketIds([linkedTicketId.value]);
    }
    if (selectedTicketIds.value.length) {
      persistTicketSelection(selectedTicketIds.value);
    }
    ticketSelectionReady.value = true;
  },
  { immediate: true },
);

watchEffect(() => {
  if (!quote.isLoading) {
    breadcrumbStore.setValue(referenceLabel.value);
  }
});

watch(
  () => [quote.data?.latest_calculation, quote.data?.calculation_error],
  ([latestCalculation, calculationError]) => {
    applyPricingSnapshot({
      latest_calculation: latestCalculation ?? null,
      calculation_error: calculationError ?? null,
    });
  },
  { immediate: true, deep: true },
);

watch(
  () => quote.data?.id,
  (id) => {
    if (!id) return;
    aiMessageLoadedFor.value = null;
    sendMessageEdited.value = false;
    sendMessage.value = "";
  },
);

const ensureVersion = () => {
  if (!quote.data.versions?.length) {
    quote.data.versions = [{ dates: [], resources: [] }];
  }
  return quote.data.versions[0];
};

const updateVersion = (val) => {
  if (!editable.value) return;
  if (!quote.data.versions?.length) {
    quote.data.versions = [val];
  } else {
    quote.data.versions[0] = val;
  }
};

const updateEventRisk = (val) => {
  if (!editable.value) return;
  const version = ensureVersion();
  version.event_risk = val;
};

const updatePtsRisk = (val) => {
  if (!editable.value) return;
  const version = ensureVersion();
  version.pts_risk = { ...val, return_time: null, calculated_finish_at: null };
  syncPtsDateWindowFromRisk();
};

const applyPtsEmailExtraction = async (payload) => {
  if (!editable.value) return;
  const extractedVersion = payload?.draft?.versions?.[0];
  if (!extractedVersion?.pts_risk) return;
  const version = ensureVersion();
  updatePtsRisk({ ...version.pts_risk, ...extractedVersion.pts_risk });
  const resources = extractedVersion.dates?.[0]?.resources;
  if (resources?.length && version.dates?.[0]) {
    const existing = version.dates[0].resources ?? [];
    for (const resource of resources) {
      const match = existing.find((entry) => Number(entry.product_id) === Number(resource.product_id));
      if (match) match.quantity = resource.quantity;
      else existing.push({ ...resource });
    }
    version.dates[0].resources = existing;
  }
  if (payload.draft.description) updateDescription(payload.draft.description);
  if (payload.draft.notes && !quote.data.notes) quote.data.notes = payload.draft.notes;
  if (payload.matched_customer?.id) await updateCustomer(payload.matched_customer);
  if (payload.matched_recipient_contact?.id) updateRecipientContact(payload.matched_recipient_contact);
};

const extractDateTimeParts = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const directMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (directMatch) {
    return {
      date: directMatch[1],
      time: `${directMatch[2]}:00`,
    };
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;

  const pad = (part) => String(part).padStart(2, "0");
  return {
    date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
    time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:00`,
  };
};

const syncPtsDateWindowFromRisk = () => {
  if (!isPts()) return;

  const version = ensureVersion();
  const risk = version?.pts_risk ?? {};
  const pickup = extractDateTimeParts(risk.pickup_time);
  const dropoff = extractDateTimeParts(risk.calculated_finish_at);

  const pickupAddress = String(risk.pickup_address ?? "").trim();
  const dropoffAddress = String(risk.dropoff_address ?? "").trim();
  const pickupLocation = String(risk.pickup_location ?? "").trim();
  const dropoffLocation = String(risk.dropoff_location ?? "").trim();
  const routeAddress = [pickupAddress, dropoffAddress]
    .filter(Boolean)
    .join(" -> ");
  const routeName = [pickupLocation, dropoffLocation]
    .filter(Boolean)
    .join(" -> ");

  const existingDates = Array.isArray(version?.dates) ? version.dates : [];
  if (!existingDates.length && !pickup) return;
  if (!pickup && !dropoff && !routeAddress) return;

  const firstDate = existingDates[0] ?? {};
  const nextFirstDate = {
    ...firstDate,
    location_name: routeName || firstDate.location_name || null,
    location_address: routeAddress || firstDate.location_address || null,
    resources: Array.isArray(firstDate.resources) ? firstDate.resources : [],
  };

  if (pickup) {
    nextFirstDate.date = pickup.date;
    nextFirstDate.start_time = pickup.time;
  }

  nextFirstDate.finish_time = dropoff?.time ?? null;

  const nextDates = existingDates.length
    ? [nextFirstDate, ...existingDates.slice(1)]
    : [nextFirstDate];

  updateVersion({
    ...version,
    dates: nextDates,
  });
};

const updateCustomer = async (customer) => {
  if (!editable.value) return;
  if (!customer?.id) return;
  quote.data.customer_id = customer.id;
  quote.data.customer = customer;

  if (!quote.data?.recipient_contact_id && customer.contact?.id) {
    updateRecipientContact(customer.contact);
  }

  try {
    const response = await apiClient.get(`/api/customer/${customer.id}`);
    const fullCustomer = response.data?.data ?? response.data ?? customer;
    quote.data.customer = fullCustomer;

    const contacts = Array.isArray(fullCustomer.contacts)
      ? fullCustomer.contacts
      : [];
    const currentRecipientId = Number(
      quote.data?.recipient_contact_id ?? quote.data?.recipient_contact?.id,
    );
    const currentStillBelongs = contacts.some(
      (contact) => Number(contact.id) === currentRecipientId,
    );

    if (currentRecipientId > 0 && !currentStillBelongs) {
      quote.data.recipient_contact_id = null;
      quote.data.recipient_contact = null;
    }

    if (!quote.data?.recipient_contact_id) {
      const primary =
        fullCustomer.contact ??
        contacts.find(
          (contact) => Number(contact.id) === Number(fullCustomer.contact_id),
        ) ??
        null;
      if (primary) updateRecipientContact(primary);
    }
  } catch (error) {
    saveError.value =
      error.response?.data?.message ??
      "Customer selected, but their contacts could not be checked. Choose the recipient before sending.";
  }
};

const recipientContactLabel = computed(() => {
  const contact = quote.data?.recipient_contact;
  if (!contact) return "";

  if (contact.name && contact.email) {
    return `${contact.name} <${contact.email}>`;
  }

  return contact.name ?? contact.email ?? "";
});

const updateRecipientContact = (contact) => {
  if (!editable.value) return;
  if (!contact) return;

  quote.data.recipient_contact_id = contact.id;
  quote.data.recipient_contact = contact;
};

const updateDescription = (val) => {
  if (!editable.value) return;
  quote.data.description = val;
  if (quote.data?.reference) {
    quote.data.reference.name = val;
  }
};

const updateNotes = (val) => {
  if (!editable.value) return;
  quote.data.notes = val;
};

const updateQuoteFiles = (files) => {
  if (!editable.value) return;
  quote.data.files = Array.isArray(files) ? files : [];
};

const sanitizeRiskForSave = (risk) => {
  if (!risk || typeof risk !== "object") {
    return undefined;
  }

  const sanitized = { ...risk };
  delete sanitized.id;
  delete sanitized.quote_version_id;
  delete sanitized.created_at;
  delete sanitized.updated_at;
  delete sanitized.eventType;

  return sanitized;
};

const normalizeResourceForSave = (resource = {}) => ({
  ...(resource.id ? { id: resource.id } : {}),
  product_id: resource.product_id,
  quantity: Number(resource.quantity ?? 1),
  notes: resource.notes ?? null,
});

const normalizeDateForSave = (date = {}) => ({
  ...(date.id ? { id: date.id } : {}),
  date: date.date ?? null,
  start_time: date.start_time ?? null,
  finish_time: date.finish_time ?? null,
  location_name: date.location_name ?? null,
  location_address: date.location_address ?? null,
  resources: Array.isArray(date.resources)
    ? date.resources.map((resource) => normalizeResourceForSave(resource))
    : [],
});

function normalizePricingOptions(options = {}, { forSave = false } = {}) {
  const logistics =
    options?.logistics && typeof options.logistics === "object"
      ? options.logistics
      : {};
  const comparison =
    options?.comparison && typeof options.comparison === "object"
      ? options.comparison
      : {};
  const rawAdjustments = Array.isArray(options?.manual_adjustments)
    ? options.manual_adjustments
    : [];
  const rawCustomerAdjustments = Array.isArray(options?.customer_adjustments)
    ? options.customer_adjustments
    : [];

  const manualAdjustments = rawAdjustments
    .map((entry = {}) => {
      const key = String(entry?.key ?? "");
      const amount =
        entry?.amount === "" || entry?.amount === null
          ? ""
          : Number(entry?.amount ?? 0);

      return {
        key,
        amount: Number.isNaN(amount) ? "" : amount,
      };
    })
    .filter((entry) => {
      if (!forSave) return true;
      const key = entry.key.trim();
      const amount = Number(entry.amount);

      return key !== "" && !Number.isNaN(amount);
    })
    .map((entry) => ({
      key: entry.key.trim(),
      amount: forSave ? Number(Number(entry.amount).toFixed(2)) : entry.amount,
    }));

  const customerAdjustments = rawCustomerAdjustments
    .map((entry = {}) => {
      const note = String(entry?.note ?? entry?.customer_note ?? "");
      const amount =
        entry?.amount === "" || entry?.amount === null
          ? ""
          : Number(entry?.amount ?? 0);

      return {
        note,
        amount: Number.isNaN(amount) ? "" : amount,
      };
    })
    .filter((entry) => {
      if (!forSave) return true;
      const note = entry.note.trim();
      const amount = Number(entry.amount);

      return note !== "" && !Number.isNaN(amount) && amount !== 0;
    })
    .map((entry) => ({
      note: entry.note.slice(0, 1000),
      amount: forSave ? Number(Number(entry.amount).toFixed(2)) : entry.amount,
    }));

  const referenceId = Number.parseInt(comparison.reference_id ?? "", 10);
  const vehicleCountOverride = Number.parseInt(
    logistics.vehicle_count_override ?? "",
    10,
  );
  const accommodationRoomCountOverride = Number.parseInt(
    logistics.accommodation_room_count_override ?? "",
    10,
  );

  return {
    manual_adjustments: manualAdjustments,
    customer_adjustments: customerAdjustments,
    logistics: {
      allow_gap_day_stay_away: Boolean(logistics.allow_gap_day_stay_away),
      allow_first_day_accommodation: Boolean(
        logistics.allow_first_day_accommodation,
      ),
      allow_last_day_accommodation:
        Object.prototype.hasOwnProperty.call(
          logistics,
          "allow_last_day_accommodation",
        )
          ? Boolean(logistics.allow_last_day_accommodation)
          : true,
      vehicle_count_override:
        !Number.isNaN(vehicleCountOverride) && vehicleCountOverride > 0
          ? vehicleCountOverride
          : null,
      accommodation_room_count_override:
        !Number.isNaN(accommodationRoomCountOverride) &&
        accommodationRoomCountOverride > 0
          ? accommodationRoomCountOverride
          : null,
    },
    comparison: {
      reference_id:
        !Number.isNaN(referenceId) && referenceId > 0 ? referenceId : null,
    },
  };
}

const activePricingOptions = computed(() =>
  normalizePricingOptions(quote.data?.versions?.[0]?.pricing_options ?? {}),
);

function setPricingOptions(nextOptions = {}) {
  if (!canEditAdvancedPricing.value) return;
  const version = ensureVersion();
  updateVersion({
    ...version,
    pricing_options: normalizePricingOptions(nextOptions),
  });
  if (isReady.value) {
    debouncedRecalculateSummary();
  }
}

function updateLogisticsPricingOption(key, checked) {
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    logistics: {
      ...current.logistics,
      [key]: Boolean(checked),
    },
  });
}

function updateLogisticsVehicleCountOverride(value) {
  const parsed = Number.parseInt(value ?? "", 10);
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    logistics: {
      ...current.logistics,
      vehicle_count_override:
        !Number.isNaN(parsed) && parsed > 0 ? parsed : null,
    },
  });
}

function updateAccommodationRoomCountOverride(value) {
  const parsed = Number.parseInt(value ?? "", 10);
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    logistics: {
      ...current.logistics,
      accommodation_room_count_override:
        !Number.isNaN(parsed) && parsed > 0 ? parsed : null,
    },
  });
}

function addManualAdjustment() {
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    manual_adjustments: [
      ...current.manual_adjustments,
      { key: "Adjustment", amount: 0 },
    ],
  });
}

function updateManualAdjustment(index, field, value) {
  const current = activePricingOptions.value;
  const manualAdjustments = current.manual_adjustments.map((entry, entryIndex) =>
    entryIndex === index ? { ...entry, [field]: value } : entry,
  );
  setPricingOptions({
    ...current,
    manual_adjustments: manualAdjustments,
  });
}

function removeManualAdjustment(index) {
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    manual_adjustments: current.manual_adjustments.filter(
      (_, entryIndex) => entryIndex !== index,
    ),
  });
}

function addCustomerAdjustment() {
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    customer_adjustments: [
      ...current.customer_adjustments,
      { note: "", amount: 0 },
    ],
  });
}

function updateCustomerAdjustment(index, field, value) {
  const current = activePricingOptions.value;
  const customerAdjustments = current.customer_adjustments.map(
    (entry, entryIndex) =>
      entryIndex === index ? { ...entry, [field]: value } : entry,
  );
  setPricingOptions({
    ...current,
    customer_adjustments: customerAdjustments,
  });
}

function removeCustomerAdjustment(index) {
  const current = activePricingOptions.value;
  setPricingOptions({
    ...current,
    customer_adjustments: current.customer_adjustments.filter(
      (_, entryIndex) => entryIndex !== index,
    ),
  });
}

const comparisonInvoiceReference = (entry) => entry?.reference ?? entry;

const formatReferenceSearchResult = (entry) => {
  const reference = comparisonInvoiceReference(entry);
  const label =
    [reference?.txt, reference?.name].filter(Boolean).join(" - ") ||
    `Reference #${reference?.id ?? ""}`;
  const subtotal = Number(entry?.subtotal ?? 0);

  if (entry?.reference && !Number.isNaN(subtotal)) {
    return `${label} (${formatPenceAsPresentationCurrency(subtotal)})`;
  }

  return label;
};

function selectComparisonReference(entry) {
  const reference = comparisonInvoiceReference(entry);
  const id = Number.parseInt(reference?.id ?? 0, 10);
  if (Number.isNaN(id) || id <= 0) return;
  comparisonReferenceSearchText.value = formatReferenceSearchResult(entry);
  updateComparisonReferenceId(id);
}

function updateComparisonReferenceId(value) {
  const current = activePricingOptions.value;
  const referenceId = Number.parseInt(value, 10);
  setPricingOptions({
    ...current,
    comparison: {
      reference_id:
        !Number.isNaN(referenceId) && referenceId > 0 ? referenceId : null,
    },
  });
}

function clearComparisonReference() {
  comparisonReferenceSearchText.value = "";
  updateComparisonReferenceId(null);
}

const normalizeVersionForSave = (version = {}) => {
  const payload = {
    ...(version.id ? { id: version.id } : {}),
    name: version.name ?? null,
    valid_until: version.valid_until ?? null,
    risk_level: version.risk_level ?? null,
    reviewed_at: version.reviewed_at ?? null,
    dates: Array.isArray(version.dates)
      ? version.dates.map((date) => normalizeDateForSave(date))
      : [],
  };

  if (canAccessAdvancedPricing.value) {
    payload.pricing_options = normalizePricingOptions(
      version.pricing_options ?? {},
      { forSave: true },
    );
  }

  const eventRisk = sanitizeRiskForSave(version.event_risk);
  if (eventRisk) {
    payload.event_risk = eventRisk;
  }

  const ptsRisk = sanitizeRiskForSave(version.pts_risk);
  if (ptsRisk) {
    payload.pts_risk = ptsRisk;
  }

  return payload;
};

function normalizeQuantityOverrideMap(overrides = {}) {
  const normalized = {};

  Object.entries(overrides ?? {}).forEach(([lineKey, quantity]) => {
    const key = String(lineKey ?? "").trim();
    const parsedQuantity = Number.parseInt(quantity, 10);
    if (!key || Number.isNaN(parsedQuantity)) return;

    normalized[key] = Math.max(parsedQuantity, 0);
  });

  return normalized;
}

function lineOverrideMapKey(line) {
  const lineKey = String(line?.line_key ?? "").trim();
  if (lineKey) return lineKey;

  const lineId = Number.parseInt(line?.id ?? 0, 10);
  if (!Number.isNaN(lineId) && lineId > 0) return `id:${lineId}`;

  const fallback = [
    String(line?.component ?? "").trim(),
    String(line?.service_date ?? "").trim(),
    String(line?.label ?? "").trim(),
    String(line?.notes ?? "").trim(),
    String(line?.unit_rate ?? "").trim(),
    String(line?.units ?? "").trim(),
  ].join("|");
  if (fallback.replace(/\|/g, "").trim() !== "") {
    return `fallback:${fallback}`;
  }

  return "";
}

function quantityOverrideSignature(overrides = {}) {
  const normalized = normalizeQuantityOverrideMap(overrides);
  const sorted = Object.keys(normalized)
    .sort((a, b) => a.localeCompare(b))
    .map((lineKey) => [lineKey, normalized[lineKey]]);

  return JSON.stringify(sorted);
}

function setLineQuantityOverrides(nextOverrides = {}) {
  const normalized = normalizeQuantityOverrideMap(nextOverrides);
  if (
    quantityOverrideSignature(normalized) ===
    quantityOverrideSignature(lineQuantityOverrides.value)
  ) {
    return false;
  }

  lineQuantityOverrides.value = normalized;
  return true;
}

function syncLineQuantityOverridesFromCalculation(calculation) {
  const lines = Array.isArray(calculation?.lines) ? calculation.lines : [];
  const overrides = {};

  lines.forEach((line) => {
    const mapKey = lineOverrideMapKey(line);
    if (!mapKey) return;

    const currentQuantity = Number.parseInt(line?.quantity ?? 0, 10);
    const baseQuantity = Number.parseInt(
      line?.base_quantity ?? currentQuantity,
      10,
    );
    const normalizedCurrent = Number.isNaN(currentQuantity)
      ? 0
      : Math.max(currentQuantity, 0);
    const normalizedBase = Number.isNaN(baseQuantity)
      ? normalizedCurrent
      : Math.max(baseQuantity, 0);
    const isOverridden =
      Boolean(line?.is_quantity_overridden) ||
      normalizedCurrent !== normalizedBase;

    if (isOverridden) {
      overrides[mapKey] = normalizedCurrent;
    }
  });

  setLineQuantityOverrides(overrides);
}

function lineQuantityValue(line) {
  const lineKey = lineOverrideMapKey(line);
  if (
    lineKey &&
    Object.prototype.hasOwnProperty.call(lineQuantityOverrides.value, lineKey)
  ) {
    return Math.max(Number.parseInt(lineQuantityOverrides.value[lineKey], 10), 0);
  }

  const quantity = Number.parseInt(line?.quantity ?? 0, 10);

  return Number.isNaN(quantity) ? 0 : Math.max(quantity, 0);
}

function lineBaseQuantityValue(line) {
  const baseQuantity = Number.parseInt(
    line?.base_quantity ?? line?.quantity ?? 0,
    10,
  );

  return Number.isNaN(baseQuantity) ? 0 : Math.max(baseQuantity, 0);
}

function isLineQuantityOverridden(line) {
  return lineQuantityValue(line) !== lineBaseQuantityValue(line);
}

function updateLineQuantityOverride(line, rawValue) {
  if (!editable.value) return false;

  const lineKey = lineOverrideMapKey(line);
  if (!lineKey) return false;

  const parsed = Number.parseInt(rawValue, 10);
  const nextQuantity = Number.isNaN(parsed) ? 0 : Math.max(parsed, 0);
  const baseQuantity = lineBaseQuantityValue(line);
  const nextOverrides = { ...lineQuantityOverrides.value };

  if (nextQuantity === baseQuantity) {
    delete nextOverrides[lineKey];
  } else {
    nextOverrides[lineKey] = nextQuantity;
  }

  const didChange = setLineQuantityOverrides(nextOverrides);
  if (didChange && isReady.value) {
    debouncedRecalculateSummary();
  }

  return didChange;
}

async function commitLineQuantityOverride(line, rawValue) {
  if (!editable.value) return;

  updateLineQuantityOverride(line, rawValue);
  if (!isReady.value) return;

  await recalculateSummaryNow();
}

function buildLineQuantityOverridePayload() {
  return Object.entries(normalizeQuantityOverrideMap(lineQuantityOverrides.value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([overrideKey, quantity]) => {
      if (overrideKey.startsWith("id:")) {
        const lineId = Number.parseInt(overrideKey.slice(3), 10);
        if (!Number.isNaN(lineId) && lineId > 0) {
          return {
            line_id: lineId,
            quantity,
          };
        }
      }

      return {
        line_key: overrideKey,
        quantity,
      };
    });
}

const getSavePayload = ({ refreshPricing = false } = {}) => ({
  customer_id: quote.data?.customer_id ?? quote.data?.customer?.id ?? null,
  recipient_contact_id:
    quote.data?.recipient_contact_id ??
    quote.data?.recipient_contact?.id ??
    null,
  reference_type_id:
    quote.data?.reference_type_id ?? quote.data?.reference_type?.id ?? null,
  description: quote.data?.description ?? null,
  notes: quote.data?.notes ?? null,
  status: quote.data?.status ?? null,
  refresh_pricing: refreshPricing,
  line_quantity_overrides: buildLineQuantityOverridePayload(),
  versions: Array.isArray(quote.data?.versions)
    ? quote.data.versions.map((version) => normalizeVersionForSave(version))
    : [],
});

const getSaveSignature = (payload) => {
  const stablePayload = { ...(payload ?? {}) };
  delete stablePayload.refresh_pricing;
  return JSON.stringify(stablePayload);
};

function syncPersistenceIds(serverQuote, syncCalculatedTimes = false) {
  const localVersions = Array.isArray(quote.data?.versions)
    ? quote.data.versions
    : [];
  const serverVersions = Array.isArray(serverQuote?.versions)
    ? serverQuote.versions
    : [];
  const unusedVersions = serverVersions.slice();

  localVersions.forEach((localVersion) => {
    const versionMatchIndex = unusedVersions.findIndex(
      (serverVersion) =>
        (localVersion?.id && Number(serverVersion?.id) === Number(localVersion.id)) ||
        (!localVersion?.id &&
          String(serverVersion?.name ?? "") === String(localVersion?.name ?? "")),
    );
    const fallbackIndex = versionMatchIndex >= 0 ? versionMatchIndex : 0;
    const serverVersion = unusedVersions[fallbackIndex];
    if (!serverVersion) return;
    unusedVersions.splice(fallbackIndex, 1);
    if (!localVersion.id && serverVersion.id) localVersion.id = serverVersion.id;
    if (syncCalculatedTimes && isPts() && localVersion.pts_risk && serverVersion.pts_risk) {
      localVersion.pts_risk.return_time = serverVersion.pts_risk.return_time;
      localVersion.pts_risk.calculated_finish_at = serverVersion.pts_risk.calculated_finish_at;
    }

    const localDates = Array.isArray(localVersion?.dates) ? localVersion.dates : [];
    const unusedDates = Array.isArray(serverVersion?.dates)
      ? serverVersion.dates.slice()
      : [];
    localDates.forEach((localDate) => {
      const dateMatchIndex = unusedDates.findIndex(
        (serverDate) =>
          (localDate?.id && Number(serverDate?.id) === Number(localDate.id)) ||
          (!localDate?.id &&
            String(serverDate?.date ?? "") === String(localDate?.date ?? "") &&
            String(serverDate?.start_time ?? "") ===
              String(localDate?.start_time ?? "") &&
            (isPts() || String(serverDate?.finish_time ?? "") ===
              String(localDate?.finish_time ?? ""))),
      );
      if (dateMatchIndex < 0) return;
      const serverDate = unusedDates.splice(dateMatchIndex, 1)[0];
      if (!localDate.id && serverDate.id) localDate.id = serverDate.id;
      if (syncCalculatedTimes && isPts()) localDate.finish_time = serverDate.finish_time;

      const localResources = Array.isArray(localDate?.resources)
        ? localDate.resources
        : [];
      const unusedResources = Array.isArray(serverDate?.resources)
        ? serverDate.resources.slice()
        : [];
      localResources.forEach((localResource) => {
        const resourceMatchIndex = unusedResources.findIndex(
          (serverResource) =>
            (localResource?.id &&
              Number(serverResource?.id) === Number(localResource.id)) ||
            (!localResource?.id &&
              Number(serverResource?.product_id ?? serverResource?.product?.id) ===
                Number(localResource?.product_id ?? localResource?.product?.id)),
        );
        if (resourceMatchIndex < 0) return;
        const serverResource = unusedResources.splice(resourceMatchIndex, 1)[0];
        if (!localResource.id && serverResource.id) {
          localResource.id = serverResource.id;
        }
      });
    });
  });
}

function applyPricingSnapshot(payload) {
  pricing.value = payload?.latest_calculation ?? null;
  pricingError.value = payload?.calculation_error ?? null;
  syncLineQuantityOverridesFromCalculation(pricing.value);
}

const quoteNameValue = () =>
  quote.data?.reference?.name || quote.data?.description || "";

const formatPenceAsPresentationCurrency = (value, appendVatSuffix = false) => {
  const amount = Number(value ?? 0);
  const formatted = `£${(amount / 100).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const masked = maskPoundAmount(formatted);
  if (appendVatSuffix && pricing.value?.is_vatable) {
    return `${masked} + VAT`;
  }
  return masked;
};

const formatSignedPercentage = (value) => {
  const amount = Number(value ?? 0);
  if (Number.isNaN(amount)) return "0%";

  return `${amount > 0 ? "+" : ""}${amount.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const formatSignedPenceAsPresentationCurrency = (value) => {
  const amount = Number(value ?? 0);
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}${formatPenceAsPresentationCurrency(Math.abs(amount))}`;
};

const formatSummaryDate = (value) => {
  if (!value) return "Overall";

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
const formatDateTimeLabel = (value) => {
  if (!value) return "recently";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const unlockedQuoteMessage = computed(() => {
  if (!quote.data?.accepted_at || isLocked.value || !quote.data?.unlocked_at) {
    return "";
  }

  const unlockedAt = formatDateTimeLabel(quote.data.unlocked_at);
  const unlockedBy = quote.data?.unlocked_by?.name
    ? ` by ${quote.data.unlocked_by.name}`
    : "";

  return `Unlocked for editing ${unlockedAt}${unlockedBy}.`;
});

const pricingRoundingAdjustment = computed(() =>
  Number(
    pricing.value?.rounding_adjustment ??
      pricing.value?.meta?.tax?.rounding_adjustment ??
      0,
  ),
);

const pricingRoundingIncrement = computed(() =>
  Number(
    pricing.value?.rounding_increment ??
      pricing.value?.meta?.tax?.rounding_increment ??
      0,
  ),
);

const summaryGroups = computed(() => {
  const lines = Array.isArray(pricing.value?.lines) ? pricing.value.lines : [];
  const groups = new Map();

  lines.forEach((line) => {
    const key = line?.service_date ?? "__overall__";
    const existing = groups.get(key) ?? {
      key,
      label: key === "__overall__" ? "Overall" : formatSummaryDate(key),
      subtotal: 0,
      lines: [],
    };

    existing.lines.push(line);
    existing.subtotal += Number(line?.subtotal ?? 0);
    groups.set(key, existing);
  });

  if (pricingRoundingAdjustment.value > 0) {
    const overallGroup = groups.get("__overall__") ?? {
      key: "__overall__",
      label: "Overall",
      subtotal: 0,
      lines: [],
    };

    overallGroup.lines.push({
      id: "rounding-adjustment",
      label: "Commercial rounding",
      component: "rounding_adjustment",
      notes: `Rounded up to nearest ${formatPenceAsPresentationCurrency(pricingRoundingIncrement.value, false)}`,
      subtotal: pricingRoundingAdjustment.value,
      is_synthetic: true,
    });
    overallGroup.subtotal += pricingRoundingAdjustment.value;
    groups.set("__overall__", overallGroup);
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.key === "__overall__") return 1;
    if (b.key === "__overall__") return -1;
    return String(a.key).localeCompare(String(b.key));
  });
});

const logisticsSummary = computed(() => {
  const snapshot = pricing.value?.meta?.logistics?.summary ?? {};
  const lines = Array.isArray(pricing.value?.lines) ? pricing.value.lines : [];

  const fallbackFuelSubtotal = lines
    .filter((line) => line?.component === "fuel")
    .reduce((sum, line) => sum + Number(line?.subtotal ?? 0), 0);
  const fallbackDriverSubtotal = lines
    .filter((line) => line?.component === "travel_driver")
    .reduce((sum, line) => sum + Number(line?.subtotal ?? 0), 0);
  const fallbackAccommodationCost = lines
    .filter((line) => line?.component === "accommodation")
    .reduce((sum, line) => sum + Number(line?.subtotal ?? 0), 0);
  const fallbackAccommodationRooms = lines
    .filter((line) => line?.component === "accommodation")
    .reduce((max, line) => Math.max(max, Number(line?.quantity ?? 0)), 0);
  const fallbackAccommodationRoomNights = lines
    .filter((line) => line?.component === "accommodation")
    .reduce(
      (sum, line) =>
        sum + Number(line?.quantity ?? 0) * Number(line?.units ?? 0),
      0,
    );

  return {
    travelFuelSubtotal: Number(
      snapshot.travel_fuel_subtotal ?? fallbackFuelSubtotal,
    ),
    travelDriverSubtotal: Number(
      snapshot.travel_driver_subtotal ?? fallbackDriverSubtotal,
    ),
    travelTotal: Number(
      snapshot.travel_total ?? fallbackFuelSubtotal + fallbackDriverSubtotal,
    ),
    vehicleCount: Number(snapshot.vehicle_count ?? 0),
    vehicleTypes: Array.isArray(snapshot.vehicle_types)
      ? snapshot.vehicle_types
      : [],
    accommodationRooms: Number(
      snapshot.accommodation_rooms ?? fallbackAccommodationRooms,
    ),
    accommodationRoomNights: Number(
      snapshot.accommodation_room_nights ?? fallbackAccommodationRoomNights,
    ),
    accommodationCost: Number(
      snapshot.accommodation_cost ?? fallbackAccommodationCost,
    ),
  };
});

const adminFeeSummary = computed(() => {
  const lines = Array.isArray(pricing.value?.lines) ? pricing.value.lines : [];
  const subtotal = lines
    .filter((line) => line?.component === "admin_fee")
    .reduce((sum, line) => sum + Number(line?.subtotal ?? 0), 0);
  const configuredRate = Number(pricing.value?.meta?.logistics?.admin_fee ?? 0);

  return {
    subtotal,
    configuredRate,
    pendingRecalculation: configuredRate > 0 && subtotal <= 0,
  };
});

const comparisonSummary = computed(() => {
  const comparison = pricing.value?.meta?.comparison;

  return comparison && typeof comparison === "object" ? comparison : null;
});

watch(
  comparisonSummary,
  (summary) => {
    if (!summary || summary.status !== "ready") return;
    const label = [summary.reference_label, summary.reference_name]
      .filter(Boolean)
      .join(" - ");
    if (label) {
      comparisonReferenceSearchText.value = label;
    }
  },
  { immediate: true },
);

const isQuantityEditableLine = (line) =>
  !line?.is_synthetic &&
  line?.component !== "manual_adjustment" &&
  line?.component !== "customer_adjustment" &&
  line?.meta?.quantity_locked !== true;

const normalizeAddressValue = (value) =>
  (value ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");
const normalizeAddressText = (value) =>
  (value ?? "").toString().trim().replace(/\s+/g, " ");
const mergeVenueAddressList = (...collections) => {
  const merged = [];
  const seen = new Set();
  const addAddress = (value) => {
    const normalized = normalizeAddressText(value);
    if (!normalized) return;

    const key = normalizeAddressValue(normalized);
    if (!key || seen.has(key)) return;

    seen.add(key);
    merged.push(normalized);
  };

  collections.forEach((collection) => {
    if (Array.isArray(collection)) {
      collection.forEach(addAddress);
      return;
    }
    addAddress(collection);
  });

  return merged;
};

const defaultEventLocationAddress = computed(() => {
  const version = quote.data?.versions?.[0] ?? {};
  const risk = version.event_risk ?? {};
  const riskAddress = normalizeAddressText(risk.address);
  if (riskAddress) return riskAddress;

  const venueAddresses = Array.isArray(risk.venue_addresses)
    ? risk.venue_addresses
    : [];
  const venueAddress = venueAddresses
    .map((value) => normalizeAddressText(value))
    .filter(Boolean)
    .slice(-1)[0];
  if (venueAddress) return venueAddress;

  return (
    (Array.isArray(version.dates) ? version.dates : [])
      .map((date) => normalizeAddressText(date?.location_address))
      .find(Boolean) ?? null
  );
});

const addVenueAddressFromChat = (address) => {
  if (!editable.value) return;

  const trimmed = normalizeAddressText(address);
  if (!trimmed) return;

  const version = ensureVersion();
  const currentRisk = version.event_risk ?? {};
  const currentVenueAddresses = Array.isArray(currentRisk.venue_addresses)
    ? currentRisk.venue_addresses
    : [];
  const nextVenueAddresses = mergeVenueAddressList(
    currentVenueAddresses,
    trimmed,
  );
  const riskUpdates = {};
  if (nextVenueAddresses.length !== currentVenueAddresses.length) {
    riskUpdates.venue_addresses = nextVenueAddresses;
  }
  const currentRiskAddress = normalizeAddressText(currentRisk.address);
  const dates = Array.isArray(version.dates) ? version.dates : [];
  const currentRiskAddressKey = normalizeAddressValue(currentRiskAddress);
  const dateAddressKeys = Array.from(
    new Set(
      dates
        .map((entry) => normalizeAddressValue(entry?.location_address))
        .filter(Boolean),
    ),
  );
  const replaceableAddressKey =
    currentRiskAddressKey ||
    (dateAddressKeys.length === 1 ? dateAddressKeys[0] : "");
  const allDatesUseCurrentOrBlank =
    dates.length > 0 &&
    replaceableAddressKey &&
    dates.every((entry) => {
      const entryAddress = normalizeAddressText(entry?.location_address);
      return (
        !entryAddress ||
        normalizeAddressValue(entryAddress) === replaceableAddressKey
      );
    });
  const shouldPromotePrimaryAddress =
    !currentRiskAddress || dates.length <= 1 || allDatesUseCurrentOrBlank;

  if (shouldPromotePrimaryAddress) {
    riskUpdates.address = trimmed;
  }

  let nextVersion = version;
  if (Object.keys(riskUpdates).length) {
    nextVersion = {
      ...nextVersion,
      event_risk: {
        ...currentRisk,
        ...riskUpdates,
      },
    };
  }

  if (!dates.length) {
    if (Object.keys(riskUpdates).length) {
      updateVersion(nextVersion);
    }
    return;
  }

  let changed = false;
  const nextDates = dates.map((entry) => {
    const currentAddress = normalizeAddressText(entry?.location_address);
    const shouldApplyToDate =
      dates.length === 1 ||
      !currentAddress ||
      (replaceableAddressKey &&
        normalizeAddressValue(currentAddress) === replaceableAddressKey);

    if (!shouldApplyToDate || currentAddress === trimmed) return entry;

    changed = true;
    return {
      ...entry,
      location_address: trimmed,
      location_name: null,
    };
  });

  if (changed || Object.keys(riskUpdates).length) {
    updateVersion({
      ...nextVersion,
      dates: nextDates,
    });
  }
};

const toggleTicketSelection = (id) => {
  const normalizedId = Number(id);
  if (Number.isNaN(normalizedId)) return;
  const index = selectedTicketIds.value.indexOf(normalizedId);
  if (index === -1) {
    selectedTicketIds.value.push(normalizedId);
  } else {
    selectedTicketIds.value.splice(index, 1);
  }
};

const openReferenceTicket = (id) => {
  const normalizedId = Number(id);
  if (Number.isNaN(normalizedId) || normalizedId <= 0) return;
  referenceTicketId.value = normalizedId;
};

const closeReferenceTicket = () => {
  referenceTicketId.value = null;
};

const isReferenceTicket = (id) =>
  Number(referenceTicketId.value) === Number(id);

function normalizeTicketIds(ids) {
  if (!Array.isArray(ids)) return [];
  const normalized = ids
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
  return Array.from(new Set(normalized));
}

function getStoredTicketSelection() {
  if (!ticketSelectionKey.value || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(ticketSelectionKey.value);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeTicketIds(parsed);
  } catch (error) {
    return [];
  }
}

function persistTicketSelection(ids) {
  if (!ticketSelectionKey.value || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      ticketSelectionKey.value,
      JSON.stringify(normalizeTicketIds(ids)),
    );
  } catch (error) {
    // Ignore localStorage failures (quota or privacy mode).
  }
}

function restoreTicketSelection() {
  const stored = getStoredTicketSelection();
  if (!stored.length) return false;
  const availableIds = new Set(
    (Array.isArray(customerTickets.value) ? customerTickets.value : [])
      .map((ticket) => Number(ticket.id))
      .filter((id) => !Number.isNaN(id)),
  );
  const filtered = stored.filter((id) => availableIds.has(id));
  if (!filtered.length) return false;
  selectedTicketIds.value = filtered;
  return true;
}

const parseTickets = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

watch(
  selectedTicketIds,
  (ids) => {
    if (!ticketSelectionReady.value) return;
    persistTicketSelection(ids);
  },
  { deep: true },
);

async function loadCustomerTickets() {
  if (!customerId.value) return;
  ticketsLoading.value = true;
  ticketsError.value = null;
  try {
    const response = await apiClient.get(
      `/api/customer/${customerId.value}/tickets`,
    );
    customerTickets.value = parseTickets(response.data);
  } catch (error) {
    ticketsError.value =
      error.response?.data?.message ?? "Unable to load tickets.";
    customerTickets.value = [];
  } finally {
    ticketsLoading.value = false;
  }
}

const saveNow = async ({ refreshPricing = false, force = false } = {}) => {
  if (!isReady.value) return null;
  if (!canManageQuote.value) return null;
  if (isLocked.value) return null;

  if (saving.value) {
    await new Promise((resolve) => {
      const stop = watch(saving, (busy) => {
        if (!busy) { stop(); resolve(); }
      });
    });
    return saveNow({ refreshPricing, force });
  }

  const payload = getSavePayload({ refreshPricing });
  const signature = getSaveSignature(payload);

  if (!force && signature === lastSavedSignature.value) {
    return null;
  }

  saving.value = true;
  saveError.value = null;
  try {
    const response = await quote.update(payload);
    const currentSignatureBeforeSync = getSaveSignature(getSavePayload());
    const responsePayload = response.data?.data ?? response.data;
    syncPersistenceIds(responsePayload, currentSignatureBeforeSync === signature);
    lastSavedSignature.value =
      currentSignatureBeforeSync === signature
        ? getSaveSignature(getSavePayload())
        : signature;
    if (refreshPricing) {
      applyPricingSnapshot(responsePayload);
    }
    lastSavedAt.value = new Date().toISOString();
    return response;
  } catch (error) {
    saveError.value =
      error.response?.data?.message ?? "Unable to save changes.";
    throw error;
  } finally {
    saving.value = false;
  }
};

const { debouncedFunction: debouncedSave, disableTimer } = useDebouncedFunction(
  saveNow,
  800,
);

const {
  debouncedFunction: debouncedRecalculateSummary,
  disableTimer: disableRecalculateSummaryTimer,
} = useDebouncedFunction(() => recalculateSummaryNow(), 250);

const recalcActive = ref(false);
let recalcAnimationTimer = null;

const triggerRecalcAnimation = () => {
  if (typeof window === "undefined") return;
  recalcActive.value = true;
  if (recalcAnimationTimer) {
    clearTimeout(recalcAnimationTimer);
  }
  recalcAnimationTimer = window.setTimeout(() => {
    recalcActive.value = false;
    recalcAnimationTimer = null;
  }, 600);
};

watch(
  () => quote.data,
  () => {
    if (!isReady.value) return;
    if (getSaveSignature(getSavePayload()) !== lastSavedSignature.value) {
      pricing.value = null;
      pricingError.value = null;
    }
    debouncedSave();
  },
  { deep: true },
);

watch(
  steps,
  (val) => {
    if (!val.length) {
      stepIndex.value = 0;
    } else if (stepIndex.value >= val.length) {
      stepIndex.value = 0;
    }
  },
  { immediate: true },
);

const currentStep = computed(
  () => steps.value[stepIndex.value] ?? steps.value[0],
);
const canGoBack = computed(() => stepIndex.value > 0);
const canGoNext = computed(() => stepIndex.value < steps.value.length - 1);

const goToStep = async (index) => {
  if (index < 0 || index >= steps.value.length || index === stepIndex.value)
    return;
  if (index > stepIndex.value) {
    const blockers = blockingIssuesBeforeStep(
      workflowIssues.value,
      steps.value,
      index,
    );
    if (blockers.length) {
      navigationError.value = blockers[0].message;
      const firstStepIndex = steps.value.findIndex(
        (step) => step.id === blockers[0].step,
      );
      if (firstStepIndex >= 0) stepIndex.value = firstStepIndex;
      await nextTick();
      document.querySelector("[data-workflow-errors]")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
  }
  disableTimer();
  try {
    await saveNow();
  } catch (error) {
    return;
  }
  navigationError.value = "";
  stepIndex.value = index;
};

const prevStep = async () => {
  if (!canGoBack.value) return;
  await goToStep(stepIndex.value - 1);
};

const nextStep = async () => {
  if (!canGoNext.value) return;
  await goToStep(stepIndex.value + 1);
};

const recalculateSummaryNow = async () => {
  if (summaryBusy.value) {
    return;
  }

  if (isLocked.value) {
    pricingError.value = summaryPrerequisiteMessage.value;
    return;
  }

  if (hasUnsavedSummaryChanges.value && !canManageQuote.value) {
    pricingError.value = summaryPrerequisiteMessage.value;
    return;
  }

  if (!hasSummaryDatesConfigured.value) {
    pricing.value = null;
    pricingError.value = null;
    return;
  }

  triggerRecalcAnimation();
  disableRecalculateSummaryTimer();
  disableTimer();
  recalculatingSummary.value = true;
  try {
    if (!quote.data?.id) return;

    if (canManageQuote.value) {
      await saveNow({ refreshPricing: true, force: true });
      return;
    }

    const svc = new QuoteService(quote.id);
    const versionId = quote.data?.versions?.[0]?.id ?? undefined;
    const response = await svc.pricing(versionId);
    const calculationPayload = response.data?.data ?? response.data ?? null;

    applyPricingSnapshot({
      latest_calculation: calculationPayload,
      calculation_error: calculationPayload?.warning ?? null,
    });
  } catch (error) {
    pricingError.value =
      error?.response?.data?.message ?? "Unable to recalculate summary.";
  } finally {
    recalculatingSummary.value = false;
  }
};

watch(
  () => [currentStep.value?.id, quote.data?.id],
  async ([stepId, quoteId]) => {
    if (stepId !== "review") return;
    if (!quoteId) return;
    if (isLocked.value) return;
    loadAiMessage();
    if (!hasSummaryDatesConfigured.value) return;
    if (pricing.value || pricingError.value) return;
    if (saving.value) return;

    try {
      await recalculateSummaryNow();
    } catch (error) {
      // The existing summary error state already handles API failures.
    }
  },
  { immediate: true },
);

async function loadAiMessage({ force = false } = {}) {
  if (!quote.data?.id) return;
  if (isLocked.value) return;
  if (aiMessageLoading.value && !force) return;
  if (sendMessageEdited.value && !force) return;
  if (
    !force &&
    aiMessageLoadedFor.value === quote.data?.id &&
    sendMessage.value?.trim()
  ) {
    return;
  }

  aiMessageLoading.value = true;
  aiMessageError.value = null;
  const svc = new QuoteService(quote.id);
  try {
    const response = await svc.aiMessage({ force });
    const message = response.data?.message ?? "";
    sendMessage.value = message;
    sendMessageEdited.value = false;
    aiMessageLoadedFor.value = quote.data?.id;
  } catch (error) {
    aiMessageError.value =
      error.response?.data?.message ?? "Unable to generate AI message.";
  } finally {
    aiMessageLoading.value = false;
  }
}

const previewQuoteDocument = async () => {
  previewLoading.value = true;
  previewError.value = null;
  previewFrameLoading.value = true;
  previewModalOpen.value = true;
  try {
    disableTimer();
    await saveNow({ refreshPricing: true, force: true });
    const svc = new QuoteService(quote.id);
    const response = await svc.document();
    const pdfBlob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "application/pdf" });
    revokePreviewFrameSrc();
    previewFrameSrc.value = window.URL.createObjectURL(pdfBlob);
  } catch (error) {
    const details = Object.values(error.response?.data?.errors ?? {}).flat().filter(Boolean);
    previewError.value = details.length
      ? details.join(" ")
      : error.response?.data?.message ?? "Unable to preview quote document.";
    previewModalOpen.value = false;
    previewFrameLoading.value = false;
    revokePreviewFrameSrc();
  } finally {
    previewLoading.value = false;
  }
};

const closePreviewModal = () => {
  previewModalOpen.value = false;
  previewFrameLoading.value = false;
  revokePreviewFrameSrc();
};

const onPreviewFrameLoad = () => {
  previewFrameLoading.value = false;
};

const onPreviewModalKeydown = (event) => {
  if (!previewModalOpen.value) return;
  if (event.key !== "Escape") return;
  event.preventDefault();
  closePreviewModal();
};

const revokePreviewFrameSrc = () => {
  const currentSrc = String(previewFrameSrc.value ?? "");
  if (
    typeof window !== "undefined" &&
    currentSrc.startsWith("blob:") &&
    typeof window.URL?.revokeObjectURL === "function"
  ) {
    window.URL.revokeObjectURL(currentSrc);
  }
  previewFrameSrc.value = "";
};

watch(previewModalOpen, (open) => {
  if (typeof window === "undefined") return;

  if (open) {
    window.addEventListener("keydown", onPreviewModalKeydown);
    return;
  }

  window.removeEventListener("keydown", onPreviewModalKeydown);
});

const sendQuote = async () => {
  if (isLocked.value) {
    sendError.value =
      "Unlock this accepted quote before making further changes.";
    return;
  }

  if (!quote.data?.sending_enabled) {
    sendError.value =
      "Quote sending is temporarily disabled while workflow changes are under review.";
    return;
  }
  if (!sendConsent.value) {
    sendError.value =
      "Send cancelled: explicit confirmation is required before sending this quote to the customer.";
    return;
  }

  sending.value = true;
  sendError.value = null;
  try {
    disableTimer();
    await saveNow();
    const svc = new QuoteService(quote.id);
    const payload = {};
    const message = (sendMessage.value ?? "").trim();
    if (message !== "") {
      payload.message = message;
    }
    const recipientContactId = Number(quote.data?.recipient_contact_id);
    if (!Number.isNaN(recipientContactId) && recipientContactId > 0) {
      payload.recipient_contact_id = recipientContactId;
    }
    if (linkedTicketId.value) {
      payload.ticket_id = linkedTicketId.value;
    }
    payload.confirm_send = true;
    await svc.send(payload);
    sendConsent.value = false;
    await quote.fetch();
    if (quote.data?.ticket_id) {
      activeTab.value = "ticket";
    }
  } catch (error) {
    sendError.value = error.response?.data?.message ?? "Unable to send quote.";
  } finally {
    sending.value = false;
  }
};

const unlockQuote = async () => {
  if (!canUnlockQuote.value) return;
  if (
    !window.confirm(
      "This quote has already been accepted. Unlock it to allow further edits? The unlock will be recorded in the reference history.",
    )
  ) {
    return;
  }

  unlocking.value = true;
  unlockError.value = null;

  try {
    disableTimer();
    const svc = new QuoteService(quote.id);
    await svc.unlock({ confirm_unlock: true });
    await quote.fetch();
    lastSavedSignature.value = getSaveSignature(getSavePayload());
  } catch (error) {
    unlockError.value =
      error.response?.data?.message ?? "Unable to unlock this quote.";
  } finally {
    unlocking.value = false;
  }
};

const duplicateQuote = async () => {
  if (!canDuplicateQuote.value) return;

  duplicating.value = true;
  duplicateError.value = null;

  try {
    disableTimer();
    if (!isLocked.value) {
      await saveNow();
    }

    const svc = new QuoteService(quote.id);
    const response = await svc.duplicate();
    const duplicatedQuote = response.data?.data ?? response.data;
    const duplicateId = Number(duplicatedQuote?.id ?? 0);

    if (Number.isNaN(duplicateId) || duplicateId <= 0) {
      throw new Error("Duplicate quote was created without an id.");
    }

    await router.push(`/quotes/${duplicateId}`);
    quote.configure(duplicateId);
    await nextTick();
    showToast("Quote duplicated. You are viewing the new quote.");
  } catch (error) {
    duplicateError.value =
      error.response?.data?.message ??
      error.message ??
      "Unable to duplicate this quote.";
  } finally {
    duplicating.value = false;
  }
};

const showToast = (message, tone = "success") => {
  toast.message = message;
  toast.tone = tone;
  toast.visible = true;
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false;
  }, 3500);
};

const acceptQuote = async () => {
  if (!canAcceptQuote.value) return;
  accepting.value = true;
  acceptError.value = null;

  try {
    disableTimer();
    disableRecalculateSummaryTimer();
    const accepted = await acceptCurrentQuote({
      prepare: async () => {
        await saveNow({ refreshPricing: true, force: true });
        disableTimer();
        disableRecalculateSummaryTimer();
        if (!hasPricingSummary.value) {
          throw new Error(pricingError.value || "Complete the quote details so the current price can be calculated.");
        }
        if (workflowBlockingIssues.value.length) {
          throw new Error(workflowBlockingIssues.value[0].message);
        }
        return {
          accepted: Boolean(quote.data?.accepted_at),
          sent: Boolean(quote.data?.sent_at),
          totalLabel: formatPenceAsPresentationCurrency(pricing.value?.total, true),
          datesLabel: (quote.data?.versions?.[0]?.dates ?? []).map((entry) => formatSummaryDate(entry.date)).join(", "),
        };
      },
      confirm: (message) => window.confirm(message),
      accept: async () => {
        const svc = new QuoteService(quote.id);
        await svc.accept(getSavePayload());
        await quote.fetch();
        lastSavedSignature.value = getSaveSignature(getSavePayload());
      },
    });
    if (!accepted) return;

    if (typeof window?.triggerConfetti === "function") {
      window.triggerConfetti();
    }

    const referenceId = Number(quote.data?.reference?.id);
    if (!Number.isNaN(referenceId) && referenceId > 0) {
      await router.push(`/reference/${referenceId}`);
    }
  } catch (error) {
    acceptError.value =
      error.response?.data?.message ?? error.message ?? "Unable to accept this quote.";
  } finally {
    accepting.value = false;
  }
};

const saveStatus = computed(() => {
  if (unlocking.value) return "Unlocking...";
  if (isLocked.value) return "Locked";
  if (saving.value) return "Saving...";
  if (saveError.value) return saveError.value;
  if (hasUnsavedSummaryChanges.value) return "Unsaved changes";
  if (!lastSavedAt.value) return "All changes saved";
  const time = new Date(lastSavedAt.value).toLocaleTimeString();
  return `Saved ${time}`;
});
const stepButtonClasses = (index, compact = false) => {
  const active = index === stepIndex.value;

  if (compact) {
    return active
      ? "shrink-0 rounded-full border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
      : "shrink-0 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-700";
  }

  return active
    ? "flex items-center justify-between rounded-lg border border-slate-900 px-3 py-2 text-left text-slate-900 transition dark:border-slate-200 dark:text-slate-100"
    : "flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-slate-600 transition hover:border-slate-400 dark:border-slate-700 dark:text-slate-300";
};

function handleBeforeUnload(event) {
  if (!hasUnsavedSummaryChanges.value || isLocked.value) return;
  event.preventDefault();
  event.returnValue = "";
}

onBeforeRouteLeave(async () => {
  if (!hasUnsavedSummaryChanges.value || isLocked.value) return true;
  disableTimer();
  try {
    await saveNow({ refreshPricing: true });
    return true;
  } catch (error) {
    saveError.value =
      error.response?.data?.message ??
      "Changes could not be saved. Retry before leaving this quote.";
    return false;
  }
});

onBeforeUnmount(() => {
  disableRecalculateSummaryTimer();
  disableTimer();
  if (recalcAnimationTimer) {
    clearTimeout(recalcAnimationTimer);
    recalcAnimationTimer = null;
  }
  if (toastTimeout) {
    clearTimeout(toastTimeout);
    toastTimeout = null;
  }
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", onPreviewModalKeydown);
    window.removeEventListener("beforeunload", handleBeforeUnload);
  }
  revokePreviewFrameSrc();
});
</script>

<template>
  <div v-if="!quote.isLoading" class="quote-view space-y-4 pb-24 md:pb-0">
    <div class="box">
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="space-y-2">
          <p class="text-xs uppercase tracking-[0.2em] text-slate-400">
            Quote Workspace
          </p>
          <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {{ displayName }}
          </h1>
          <div class="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span>{{ referenceLabel }}</span>
            <span>{{ quote.data?.reference_type?.name ?? "Quote" }}</span>
            <span v-if="quote.data?.customer?.company"
              >• {{ quote.data.customer.company }}</span
            >
            <span v-if="quote.data?.updated_at"
              >• Updated {{ quote.data.updated_at }}</span
            >
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-3">
          <span class="text-xs uppercase tracking-wide text-slate-400">{{
            saveStatus
          }}</span>
          <div v-if="hasTicketTab" class="flex items-center gap-2">
            <button
              class="btn"
              type="button"
              :class="activeTab === 'quote' ? 'accent' : ''"
              @click.prevent="activeTab = 'quote'"
            >
              Quote
            </button>
            <button
              class="btn"
              type="button"
              :class="activeTab === 'ticket' ? 'accent' : ''"
              @click.prevent="activeTab = 'ticket'"
            >
              Ticket
            </button>
          </div>
          <button
            v-if="isEvent() && activeTab === 'quote'"
            class="btn chat"
            type="button"
            :disabled="!editable"
            @click.prevent="riskChatRef?.startAssessment()"
          >
            <Icon.ChatBubbleLeftRightIcon class="icon" /> Chat
          </button>
          <button
            v-if="canManageQuote && quote.data?.id"
            class="group relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-600 bg-cyan-600 text-white shadow-sm transition hover:border-cyan-500 hover:bg-cyan-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 dark:border-cyan-500 dark:bg-cyan-500 dark:text-slate-950 dark:hover:border-cyan-400 dark:hover:bg-cyan-400"
            type="button"
            :disabled="!canDuplicateQuote"
            :aria-label="duplicating ? 'Duplicating quote' : 'Duplicate quote'"
            :title="duplicating ? 'Duplicating quote' : 'Duplicate quote'"
            @click.prevent="duplicateQuote"
          >
            <Icon.DocumentDuplicateIcon
              class="h-4 w-4"
              :class="{ 'animate-pulse': duplicating }"
            />
            <span
              class="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs font-semibold text-white shadow-lg group-hover:block group-focus-visible:block dark:bg-slate-100 dark:text-slate-900"
            >
              {{ duplicating ? "Duplicating quote" : "Duplicate quote" }}
            </span>
          </button>
          <DeleteButton
            v-if="canDeleteQuote && quote.data?.id && editable"
            title="Delete Quote"
            :url="'/api/quote/' + quote.data.id"
            redirect="/quotes"
          />
          <QuoteAcceptAction
            v-if="canManageQuote && quote.data?.id"
            :accepted="Boolean(quote.data.accepted_at)"
            :locked="isLocked"
            :busy="accepting"
            :disabled="!canAcceptQuote"
            @accept="acceptQuote"
          />
          <button
            v-if="canUnlockQuote"
            class="btn accent rounded"
            type="button"
            @click.prevent="unlockQuote"
            :disabled="unlocking"
          >
            <Icon.LockOpenIcon class="icon" />
            {{ unlocking ? "Unlocking..." : "Unlock Quote" }}
          </button>
          <router-link
            v-if="quote.data?.reference?.id"
            class="btn"
            :to="`/reference/${quote.data.reference.id}`"
          >
            <Icon.ArrowTopRightOnSquareIcon class="icon" />
            View Reference
          </router-link>
        </div>
      </div>
    </div>

    <div v-if="sendError" class="box text-sm text-rose-500">
      {{ sendError }}
    </div>
    <div v-if="acceptError" class="box text-sm text-rose-500">
      {{ acceptError }}
    </div>
    <div v-if="unlockError" class="box text-sm text-rose-500">
      {{ unlockError }}
    </div>
    <div v-if="duplicateError" class="box text-sm text-rose-500">
      {{ duplicateError }}
    </div>
    <div v-if="saveError" class="box flex flex-wrap items-center justify-between gap-3 text-sm text-rose-600" role="alert">
      <span>{{ saveError }}</span>
      <button class="btn" type="button" :disabled="saving" @click.prevent="saveNow">
        {{ saving ? "Retrying..." : "Retry save" }}
      </button>
    </div>
    <div
      v-if="isLocked"
      class="box border border-amber-200 bg-amber-50 text-amber-900"
    >
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div class="space-y-1">
          <p class="text-xs uppercase tracking-[0.18em] text-amber-700">
            Accepted Quote Locked
          </p>
          <p class="text-sm">
            {{ lockedQuoteMessage }}
          </p>
        </div>
        <button
          class="btn accent"
          type="button"
          :disabled="unlocking"
          @click.prevent="unlockQuote"
        >
          <Icon.LockOpenIcon class="icon" />
          {{ unlocking ? "Unlocking..." : "Unlock for Edits" }}
        </button>
      </div>
    </div>
    <div
      v-else-if="unlockedQuoteMessage"
      class="box border border-amber-200 bg-amber-50 text-sm text-amber-900"
    >
      {{ unlockedQuoteMessage }}
    </div>

    <div
      v-if="activeTab === 'quote'"
      class="grid grid-cols-1 gap-4"
      :class="quoteWorkspaceGridClasses"
    >
      <div class="space-y-4">
        <div class="box">
          <div
            class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <p class="text-xs uppercase tracking-[0.2em] text-slate-400">
                Step {{ stepIndex + 1 }} of {{ steps.length }}
              </p>
              <h2
                class="text-xl font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ currentStep?.label }}
              </h2>
            </div>
            <div class="flex items-center gap-2">
              <button
                v-if="canGoBack"
                class="btn"
                type="button"
                @click.prevent="prevStep"
              >
                Back
              </button>
              <button
                v-if="canGoNext"
                class="btn accent rounded"
                type="button"
                @click.prevent="nextStep"
              >
                Next
              </button>
            </div>
          </div>
          <div class="mt-4 xl:hidden">
            <div class="flex gap-2 overflow-x-auto pb-1">
              <button
                v-for="(step, index) in steps"
                :key="step.id"
                type="button"
                :class="stepButtonClasses(index, true)"
                @click.prevent="goToStep(index)"
              >
                <span>{{ step.label }}</span>
                <span
                  v-if="stepIssueCount(step.id)"
                  class="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-700"
                >{{ stepIssueCount(step.id) }}</span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="navigationError || currentStepIssues.length"
          data-workflow-errors
          class="box border border-amber-200 bg-amber-50 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
          role="alert"
        >
          <p class="font-semibold">
            {{ navigationError || "Check this step before continuing" }}
          </p>
          <ul v-if="currentStepIssues.length" class="mt-2 list-disc space-y-1 pl-5">
            <li v-for="entry in currentStepIssues" :key="`${entry.field}:${entry.message}`">
              {{ entry.message }}
            </li>
          </ul>
        </div>

        <section v-if="isEvent() && currentStep?.id === 'event-basics'">
          <QuoteEventBasics
            :risk="quote.data.versions?.[0]?.event_risk ?? {}"
            :entries="quote.data.versions?.[0] ?? { dates: [] }"
            :customer="quote.data?.customer"
            :recipientContact="quote.data?.recipient_contact"
            :editable="editable"
            @update:risk="updateEventRisk"
            @update:customer="updateCustomer"
            @update:recipient-contact="updateRecipientContact"
            @update:entries="updateVersion"
          />
          <FormBox header="Quote Notes">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500">Quote Name</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quoteNameValue()"
                  v-if="editable"
                  @input="updateDescription($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quoteNameValue() || "Quote name not provided"
                }}</span>
              </div>
              <div>
                <label class="block text-xs text-slate-500">Notes</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quote.data.notes ?? ''"
                  v-if="editable"
                  @input="updateNotes($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quote.data.notes ?? "Notes not provided"
                }}</span>
              </div>
            </div>
          </FormBox>
        </section>
        <section v-if="isEvent()" v-show="currentStep?.id === 'event-risk'">
          <QuoteEventRisk
            ref="riskChatRef"
            :risk="quote.data.versions?.[0]?.event_risk ?? {}"
            :quoteId="quote.data?.id"
            :ticketId="linkedTicketId"
            :contextTicketIds="selectedTicketIds"
            :entries="quote.data.versions?.[0] ?? { dates: [] }"
            :editable="editable"
            @update:risk="updateEventRisk"
            @update:entries="updateVersion"
            @update:description="updateDescription"
            @add:address="addVenueAddressFromChat"
          />
        </section>

        <section v-if="isPts() && currentStep?.id === 'pts-basics'">
          <QuotePtsEmailExtraction
            v-if="editable"
            :key="quote.data.id"
            :reference-type-id="Number(quote.data.reference_type_id ?? quote.data.reference_type?.id)"
            @apply="applyPtsEmailExtraction"
          />
          <FormBox header="Customer">
            <CustomerSearch
              v-if="editable"
              :customer="quote.data?.customer"
              :resetAfterSelection="false"
              @selected="updateCustomer"
            />
            <p v-else class="text-sm text-slate-600 dark:text-slate-300">
              {{ quote.data?.customer?.company ?? "Customer not provided" }}
            </p>
          </FormBox>
          <QuotePtsRisk
            :risk="quote.data.versions?.[0]?.pts_risk ?? {}"
            :editable="editable"
            @update:risk="updatePtsRisk"
          />
          <FormBox header="Route Map">
            <QuotePtsRouteMap
              :return-required="Boolean(quote.data.versions?.[0]?.pts_risk?.return_required)"
              :pickup-address="
                quote.data.versions?.[0]?.pts_risk?.pickup_address ?? ''
              "
              :dropoff-address="
                quote.data.versions?.[0]?.pts_risk?.dropoff_address ?? ''
              "
            />
          </FormBox>
          <FormBox header="Quote Notes">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500">Quote Name</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quoteNameValue()"
                  v-if="editable"
                  @input="updateDescription($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quoteNameValue() || "Quote name not provided"
                }}</span>
              </div>
              <div>
                <label class="block text-xs text-slate-500">Notes</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quote.data.notes ?? ''"
                  v-if="editable"
                  @input="updateNotes($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quote.data.notes ?? "Notes not provided"
                }}</span>
              </div>
            </div>
          </FormBox>
        </section>

        <section v-if="currentStep?.id === 'details'">
          <FormBox header="Quote Notes">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-slate-500">Quote Name</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quoteNameValue()"
                  v-if="editable"
                  @input="updateDescription($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quoteNameValue() || "Quote name not provided"
                }}</span>
              </div>
              <div>
                <label class="block text-xs text-slate-500">Notes</label>
                <textarea
                  class="w-full min-h-24"
                  :value="quote.data.notes ?? ''"
                  v-if="editable"
                  @input="updateNotes($event.target.value)"
                ></textarea>
                <span v-else class="whitespace-pre-line">{{
                  quote.data.notes ?? "Notes not provided"
                }}</span>
              </div>
            </div>
          </FormBox>
        </section>

        <section v-if="currentStep?.id === 'resources'">
          <QuoteResources
            :entries="quote.data.versions?.[0] ?? { dates: [] }"
            :editable="editable"
            :loading="quote.isLoading"
            :default-location-address="defaultEventLocationAddress"
            :manage-location-in-basics="isEvent()"
            :calculated-times="isPts()"
            @update:entries="updateVersion"
          />
          <QuoteExtras
            v-if="isEvent()"
            :entries="quote.data.versions?.[0] ?? { dates: [] }"
            :editable="editable"
            @update:entries="updateVersion"
          />
        </section>
        <section v-if="currentStep?.id === 'review'">
          <FormBox header="Summary">
            <div
              v-if="quote.data?.versions?.[0]?.dates?.length"
              class="mb-4 space-y-3"
            >
              <div>
                <p class="text-xs uppercase tracking-[0.18em] text-slate-400">
                  Resource Timeline
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  The same planner-style view used on the reference screen.
                </p>
              </div>
              <QuoteResourceTimeline
                :entries="quote.data.versions?.[0] ?? { dates: [] }"
              />
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <button
                :class="['btn flex items-center gap-2', { 'recalc-active': recalcActive }]"
                type="button"
                @click.prevent="recalculateSummaryNow"
                :disabled="!canRunSummaryRecalculation || previewLoading"
                :aria-busy="summaryBusy"
                :title="summaryPrerequisiteMessage || undefined"
              >
                <Icon.ArrowPathIcon
                  class="icon"
                  :class="{ 'animate-spin': summaryBusy }"
                />
                Recalculate summary
              </button>
              <button
                class="btn"
                type="button"
                @click.prevent="previewQuoteDocument"
                :disabled="previewLoading || workflowBlockingIssues.length > 0"
              >
                <Icon.EyeIcon class="icon" /> Preview quote PDF
              </button>
              <span v-if="summaryBusy" class="text-sm text-slate-500"
                >Recalculating summary…</span
              >
              <span v-if="previewLoading" class="text-sm text-slate-500"
                >Preparing preview…</span
              >
              <span v-else-if="summaryPrerequisiteMessage" class="text-sm text-slate-500">
                {{ summaryPrerequisiteMessage }}
              </span>
            </div>
            <div
              v-if="pricingError"
              class="mt-2 text-sm text-rose-500"
              role="alert"
            >
              {{ pricingError }}
            </div>
            <div v-if="previewError" class="mt-2 text-sm text-rose-500">
              {{ previewError }}
            </div>

            <div
              v-if="canAccessAdvancedPricing"
              class="mt-4 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700"
            >
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                @click.prevent="advancedPricingOpen = !advancedPricingOpen"
              >
                <span
                  class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                >
                  Advanced Pricing
                </span>
                <Icon.ChevronDownIcon
                  class="h-5 w-5 text-slate-500 transition-transform"
                  :class="advancedPricingOpen ? 'rotate-180' : ''"
                />
              </button>

              <div
                v-if="advancedPricingOpen"
                class="space-y-5 border-t border-slate-200 px-4 py-4 dark:border-slate-700"
              >
                <div class="grid grid-cols-1 gap-5 xl:grid-cols-3">
                  <section class="space-y-3">
                    <div
                      class="flex items-center justify-between gap-3"
                    >
                      <h3
                        class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                      >
                        Adjustments
                      </h3>
                      <button
                        class="btn btn-sm"
                        type="button"
                        :disabled="!canEditAdvancedPricing"
                        @click.prevent="addManualAdjustment"
                      >
                        <Icon.PlusIcon class="icon" />
                        Add
                      </button>
                    </div>

                    <div class="space-y-2">
                      <div
                        v-for="(adjustment, index) in activePricingOptions.manual_adjustments"
                        :key="`manual-adjustment-${index}`"
                        class="grid grid-cols-[minmax(0,1fr)_7rem_2rem] items-center gap-2"
                      >
                        <input
                          class="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          type="text"
                          placeholder="Key"
                          :disabled="!canEditAdvancedPricing"
                          :value="adjustment.key"
                          @input="
                            updateManualAdjustment(index, 'key', $event.target.value)
                          "
                        />
                        <input
                          class="w-full rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          type="number"
                          step="0.01"
                          :disabled="!canEditAdvancedPricing"
                          :value="adjustment.amount"
                          @input="
                            updateManualAdjustment(
                              index,
                              'amount',
                              $event.target.value === ''
                                ? ''
                                : Number($event.target.value)
                            )
                          "
                        />
                        <button
                          class="btn btn-sm justify-center"
                          type="button"
                          :disabled="!canEditAdvancedPricing"
                          @click.prevent="removeManualAdjustment(index)"
                        >
                          <Icon.XMarkIcon class="icon" />
                        </button>
                      </div>
                      <p
                        v-if="!activePricingOptions.manual_adjustments.length"
                        class="text-sm text-slate-500"
                      >
                        No manual adjustments.
                      </p>
                    </div>
                  </section>

                  <section class="space-y-3">
                    <h3
                      class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                    >
                      Logistics
                    </h3>
                    <label class="block text-sm">
                      <span class="text-slate-600 dark:text-slate-300">
                        Journey vehicles
                      </span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        class="input mt-1 w-full"
                        :disabled="!canEditAdvancedPricing"
                        :value="
                          activePricingOptions.logistics
                            .vehicle_count_override ?? ''
                        "
                        placeholder="Auto"
                        @input="
                          updateLogisticsVehicleCountOverride(
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <label class="block text-sm">
                      <span class="text-slate-600 dark:text-slate-300">
                        Accommodation rooms
                      </span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        class="input mt-1 w-full"
                        :disabled="!canEditAdvancedPricing"
                        :value="
                          activePricingOptions.logistics
                            .accommodation_room_count_override ?? ''
                        "
                        placeholder="Auto"
                        @input="
                          updateAccommodationRoomCountOverride(
                            $event.target.value
                          )
                        "
                      />
                    </label>
                    <label class="flex items-center justify-between gap-3 text-sm">
                      <span class="text-slate-600 dark:text-slate-300"
                        >Stay away across gap days</span
                      >
                      <input
                        type="checkbox"
                        class="h-4 w-4"
                        :disabled="!canEditAdvancedPricing"
                        :checked="
                          activePricingOptions.logistics.allow_gap_day_stay_away
                        "
                        @change="
                          updateLogisticsPricingOption(
                            'allow_gap_day_stay_away',
                            $event.target.checked
                          )
                        "
                      />
                    </label>
                    <label class="flex items-center justify-between gap-3 text-sm">
                      <span class="text-slate-600 dark:text-slate-300"
                        >First-day accommodation</span
                      >
                      <input
                        type="checkbox"
                        class="h-4 w-4"
                        :disabled="!canEditAdvancedPricing"
                        :checked="
                          activePricingOptions.logistics
                            .allow_first_day_accommodation
                        "
                        @change="
                          updateLogisticsPricingOption(
                            'allow_first_day_accommodation',
                            $event.target.checked
                          )
                        "
                      />
                    </label>
                    <label class="flex items-center justify-between gap-3 text-sm">
                      <span class="text-slate-600 dark:text-slate-300"
                        >Last-day accommodation</span
                      >
                      <input
                        type="checkbox"
                        class="h-4 w-4"
                        :disabled="!canEditAdvancedPricing"
                        :checked="
                          activePricingOptions.logistics
                            .allow_last_day_accommodation
                        "
                        @change="
                          updateLogisticsPricingOption(
                            'allow_last_day_accommodation',
                            $event.target.checked
                          )
                        "
                      />
                    </label>
                  </section>

                  <section class="space-y-3">
                    <h3
                      class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                    >
                      Last Year
                    </h3>
                    <div class="space-y-2">
                      <label class="block text-xs text-slate-500">
                        Previous invoice
                      </label>
                      <SearchableInput
                        v-if="canEditAdvancedPricing"
                        v-model="comparisonReferenceSearchText"
                        url="/api/quote/comparison-reference/search?query="
                        :output="formatReferenceSearchResult"
                        placeholder="Search invoice reference..."
                        :clear-on-select="false"
                        :auto-show-results="false"
                        @selected="selectComparisonReference"
                      />
                      <p v-else class="text-sm text-slate-600 dark:text-slate-300">
                        {{
                          comparisonReferenceSearchText ||
                          (activePricingOptions.comparison.reference_id
                            ? `Reference #${activePricingOptions.comparison.reference_id}`
                            : "No reference selected")
                        }}
                      </p>
                      <div
                        v-if="activePricingOptions.comparison.reference_id"
                        class="flex items-center justify-between gap-3 text-xs text-slate-500"
                      >
                        <span>
                          Selected reference #{{
                            activePricingOptions.comparison.reference_id
                          }}
                        </span>
                        <button
                          class="text-slate-700 underline dark:text-slate-200"
                          type="button"
                          :disabled="!canEditAdvancedPricing"
                          @click.prevent="clearComparisonReference"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div
                      v-if="comparisonSummary?.status === 'ready'"
                      class="space-y-1 text-sm"
                    >
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-slate-500">Previous</span>
                        <span
                          class="font-medium text-slate-900 dark:text-slate-100"
                        >
                          {{
                            formatPenceAsPresentationCurrency(
                              comparisonSummary.previous_total
                            )
                          }}
                        </span>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-slate-500">Current</span>
                        <span
                          class="font-medium text-slate-900 dark:text-slate-100"
                        >
                          {{
                            formatPenceAsPresentationCurrency(
                              comparisonSummary.current_total
                            )
                          }}
                        </span>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-slate-500">Change</span>
                        <span
                          class="font-semibold"
                          :class="
                            Number(comparisonSummary.difference ?? 0) >= 0
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-emerald-700 dark:text-emerald-300'
                          "
                        >
                          {{
                            formatSignedPenceAsPresentationCurrency(
                              comparisonSummary.difference
                            )
                          }}
                        </span>
                      </div>
                      <div class="flex items-center justify-between gap-3">
                        <span class="text-slate-500">Year-on-year</span>
                        <span
                          class="font-semibold"
                          :class="
                            Number(comparisonSummary.percentage_change ?? 0) >= 0
                              ? 'text-amber-700 dark:text-amber-300'
                              : 'text-emerald-700 dark:text-emerald-300'
                          "
                        >
                          {{
                            formatSignedPercentage(
                              comparisonSummary.percentage_change
                            )
                          }}
                        </span>
                      </div>
                    </div>
                    <p
                      v-else-if="comparisonSummary?.status"
                      class="text-sm text-slate-500"
                    >
                      Comparison total unavailable.
                    </p>
                  </section>
                </div>

                <section
                  class="space-y-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4 dark:border-amber-500/30 dark:bg-amber-500/5"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="space-y-1">
                      <h3
                        class="text-sm font-semibold text-slate-900 dark:text-slate-100"
                      >
                        Customer-facing Price Notes
                      </h3>
                      <span
                        class="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200"
                      >
                        Shown on quote
                      </span>
                    </div>
                    <button
                      class="btn btn-sm"
                      type="button"
                      :disabled="!canEditAdvancedPricing"
                      @click.prevent="addCustomerAdjustment"
                    >
                      <Icon.PlusIcon class="icon" />
                      Add
                    </button>
                  </div>

                  <div class="space-y-2">
                    <div
                      v-for="(adjustment, index) in activePricingOptions.customer_adjustments"
                      :key="`customer-adjustment-${index}`"
                      class="space-y-2 rounded border border-amber-200 bg-white p-3 dark:border-amber-500/30 dark:bg-slate-900"
                    >
                      <div
                        class="grid grid-cols-[minmax(0,1fr)_7rem_2rem] items-start gap-2"
                      >
                        <textarea
                          class="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          rows="2"
                          placeholder="Customer note"
                          :disabled="!canEditAdvancedPricing"
                          :value="adjustment.note"
                          @input="
                            updateCustomerAdjustment(
                              index,
                              'note',
                              $event.target.value
                            )
                          "
                        ></textarea>
                        <input
                          class="w-full rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                          type="number"
                          inputmode="decimal"
                          step="0.01"
                          placeholder="0.00"
                          :disabled="!canEditAdvancedPricing"
                          :value="adjustment.amount"
                          @input="
                            updateCustomerAdjustment(
                              index,
                              'amount',
                              $event.target.value === ''
                                ? ''
                                : Number($event.target.value)
                            )
                          "
                        />
                        <button
                          class="btn btn-sm justify-center"
                          type="button"
                          :disabled="!canEditAdvancedPricing"
                          @click.prevent="removeCustomerAdjustment(index)"
                        >
                          <Icon.XMarkIcon class="icon" />
                        </button>
                      </div>
                    </div>
                    <p
                      v-if="!activePricingOptions.customer_adjustments.length"
                      class="text-sm text-slate-500"
                    >
                      No customer-facing price notes.
                    </p>
                  </div>
                </section>
              </div>
            </div>

            <div v-if="pricing" class="mt-4 space-y-3">
              <div
                class="rounded-lg border border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between"
              >
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    Total
                  </div>
                  <div
                    class="text-xl font-semibold text-slate-900 dark:text-slate-100"
                  >
                    {{
                      formatPenceAsPresentationCurrency(
                        pricing.subtotal_ex_vat ?? pricing.total,
                      )
                    }}
                  </div>
                  <div
                    v-if="pricingRoundingAdjustment > 0"
                    class="mt-1 text-xs text-slate-500"
                  >
                    Rounded up by
                    {{
                      formatPenceAsPresentationCurrency(
                        pricingRoundingAdjustment,
                        false,
                      )
                    }}
                    to nearest
                    {{
                      formatPenceAsPresentationCurrency(
                        pricingRoundingIncrement,
                        false,
                      )
                    }}.
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    Sections
                  </div>
                  <div
                    class="text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    {{ summaryGroups.length }}
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-3 lg:grid-cols-4">
                <div
                  class="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    Travel
                  </div>
                  <div class="mt-3 space-y-2 text-sm">
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Fuel</span>
                      <span
                        class="font-medium text-slate-900 dark:text-slate-100"
                      >
                        {{
                          formatPenceAsPresentationCurrency(
                            logisticsSummary.travelFuelSubtotal,
                          )
                        }}
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Driver Pay</span>
                      <span
                        class="font-medium text-slate-900 dark:text-slate-100"
                      >
                        {{
                          formatPenceAsPresentationCurrency(
                            logisticsSummary.travelDriverSubtotal,
                          )
                        }}
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Total Travel</span>
                      <span
                        class="font-semibold text-slate-900 dark:text-slate-100"
                      >
                        {{
                          formatPenceAsPresentationCurrency(
                            logisticsSummary.travelTotal,
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  class="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    Vehicles
                  </div>
                  <div
                    class="mt-2 text-xl font-semibold text-slate-900 dark:text-slate-100"
                  >
                    {{
                      Number(logisticsSummary.vehicleCount).toLocaleString(
                        "en-GB",
                      )
                    }}
                  </div>
                  <div class="mt-3 flex flex-wrap gap-2">
                    <span
                      v-for="vehicle in logisticsSummary.vehicleTypes"
                      :key="vehicle.type"
                      class="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      {{
                        `${Number(vehicle.count ?? 0).toLocaleString("en-GB")} x ${vehicle.type}`
                      }}
                    </span>
                    <span
                      v-if="!logisticsSummary.vehicleTypes.length"
                      class="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                    >
                      0 vehicles
                    </span>
                  </div>
                </div>

                <div
                  class="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    Accommodation
                  </div>
                  <div class="mt-3 space-y-2 text-sm">
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Peak Rooms</span>
                      <span
                        class="font-medium text-slate-900 dark:text-slate-100"
                      >
                        {{
                          Number(
                            logisticsSummary.accommodationRooms,
                          ).toLocaleString("en-GB")
                        }}
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Room Nights</span>
                      <span
                        class="font-medium text-slate-900 dark:text-slate-100"
                      >
                        {{
                          Number(
                            logisticsSummary.accommodationRoomNights,
                          ).toLocaleString("en-GB")
                        }}
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Cost</span>
                      <span
                        class="font-semibold text-slate-900 dark:text-slate-100"
                      >
                        {{
                          formatPenceAsPresentationCurrency(
                            logisticsSummary.accommodationCost,
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  class="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                >
                  <div class="text-xs uppercase tracking-wide text-slate-500">
                    One-Off
                  </div>
                  <div class="mt-3 space-y-2 text-sm">
                    <div class="flex items-center justify-between gap-4">
                      <span class="text-slate-500">Admin Fee</span>
                      <span
                        class="font-semibold text-slate-900 dark:text-slate-100"
                      >
                        {{
                          formatPenceAsPresentationCurrency(
                            adminFeeSummary.subtotal,
                          )
                        }}
                      </span>
                    </div>
                    <div
                      v-if="adminFeeSummary.pendingRecalculation"
                      class="text-xs text-amber-700 dark:text-amber-300"
                    >
                      Admin fee is configured but not in this saved calculation
                      yet. Recalculate/save to apply.
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-for="group in summaryGroups"
                :key="group.key"
                class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <div
                  class="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/50"
                >
                  <div>
                    <div class="text-xs uppercase tracking-wide text-slate-500">
                      {{
                        group.key === "__overall__" ? "Undated Costs" : "Day"
                      }}
                    </div>
                    <div
                      class="text-base font-semibold text-slate-900 dark:text-slate-100"
                    >
                      {{ group.label }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-xs uppercase tracking-wide text-slate-500">
                      Subtotal
                    </div>
                    <div
                      class="text-base font-semibold text-slate-900 dark:text-slate-100"
                    >
                      {{ formatPenceAsPresentationCurrency(group.subtotal) }}
                    </div>
                  </div>
                </div>

                <div class="overflow-x-auto">
                <table
                  class="min-w-[720px] w-full divide-y divide-slate-200 text-sm dark:divide-slate-700"
                >
                  <thead
                    class="bg-slate-900 text-left text-xs font-semibold uppercase tracking-wider text-white dark:bg-slate-800 dark:text-slate-100"
                  >
                    <tr>
                      <th class="px-4 py-2">Item</th>
                      <th class="px-4 py-2 text-right">Qty</th>
                      <th class="px-4 py-2 text-right">Units</th>
                      <th class="px-4 py-2 text-right">Rate</th>
                      <th class="px-4 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    <tr v-for="line in group.lines" :key="line.id">
                      <td class="px-4 py-2">
                        <div
                          class="font-medium text-slate-900 dark:text-slate-100"
                        >
                          {{ line.label || line.product?.name || "Item" }}
                        </div>
                        <div class="text-xs text-slate-500">
                          {{ line.component }} • {{ line.notes }}
                        </div>
                      </td>
                      <td class="px-4 py-2 text-right">
                        <div
                          v-if="isQuantityEditableLine(line)"
                          class="flex flex-col items-end gap-1"
                        >
                          <input
                            type="number"
                            min="0"
                            step="1"
                            class="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            :value="lineQuantityValue(line)"
                            :disabled="!editable"
                            @input="
                              updateLineQuantityOverride(line, $event.target.value)
                            "
                            @change="
                              commitLineQuantityOverride(line, $event.target.value)
                            "
                            @blur="
                              commitLineQuantityOverride(line, $event.target.value)
                            "
                          />
                          <span
                            v-if="isLineQuantityOverridden(line)"
                            class="text-[11px] text-amber-700 dark:text-amber-300"
                          >
                            Base: {{
                              Number(lineBaseQuantityValue(line)).toLocaleString(
                                'en-GB',
                              )
                            }}
                          </span>
                        </div>
                        <span v-else-if="line.is_synthetic" class="text-slate-400">-</span>
                        <span
                          v-else
                          class="font-medium text-slate-900 dark:text-slate-100"
                        >
                          {{ Number(line.quantity ?? 0).toLocaleString("en-GB") }}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-right">
                        <span v-if="line.is_synthetic" class="text-slate-400"
                          >-</span
                        >
                        <template v-else>
                          {{
                          Number(line.units ?? 0).toLocaleString("en-GB", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })
                        }}
                        </template>
                      </td>
                      <td class="px-4 py-2 text-right">
                        <span v-if="line.is_synthetic" class="text-slate-400"
                          >-</span
                        >
                        <template v-else>
                          {{
                            formatPenceAsPresentationCurrency(line.unit_rate)
                          }}
                        </template>
                      </td>
                      <td class="px-4 py-2 text-right">
                        {{ formatPenceAsPresentationCurrency(line.subtotal) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
              <div
                v-if="!summaryGroups.length"
                class="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500"
              >
                No priced items yet.
              </div>
            </div>
            <div
              v-else-if="!pricingError"
              class="mt-4 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500"
            >
              Summary updates automatically once the quote has enough detail to
              price.
            </div>
          </FormBox>
        </section>

        <section v-if="currentStep?.id === 'review'">
          <FormBox header="Recipient">
            <div class="space-y-3">
              <ContactSearch
                v-if="editable"
                placeholder="Find or add recipient..."
                :value="recipientContactLabel"
                :autoShowResults="false"
                :customerId="customerId"
                @selected="updateRecipientContact"
              />
              <p v-else class="text-sm text-slate-600 dark:text-slate-300">
                {{ recipientContactLabel || "No recipient selected." }}
              </p>
              <p class="text-xs text-slate-500">
                Choose who should receive the quote. If this quote is linked to
                an existing ticket, replies stay on that thread.
              </p>
            </div>
          </FormBox>

          <FormBox header="Send to Customer">
            <div class="mt-4 flex flex-wrap items-center gap-3">
              <button
                class="btn"
                type="button"
                @click.prevent="previewQuoteDocument"
                :disabled="previewLoading || workflowBlockingIssues.length > 0"
              >
                <Icon.EyeIcon class="icon" /> Preview quote PDF
              </button>
              <button
                class="btn"
                type="button"
                @click.prevent="sendQuote"
                :disabled="sending || !canSendQuote"
              >
                <Icon.PaperAirplaneIcon class="icon" />
                {{ quote.data?.sent_at ? "Resend quote" : "Send quote" }}
              </button>
              <QuoteAcceptAction
                v-if="canManageQuote && quote.data?.id"
                :accepted="Boolean(quote.data.accepted_at)"
                :locked="isLocked"
                :busy="accepting"
                :disabled="!canAcceptQuote"
                @accept="acceptQuote"
              />
              <span v-if="previewLoading" class="text-sm text-slate-500">
                Preparing preview…
              </span>
            </div>

            <div class="mt-4 space-y-2">
              <div
                class="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40"
              >
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-300"
                >
                  Message to recipient
                </label>
                <textarea
                  v-model="sendMessage"
                  @input="sendMessageEdited = true"
                  class="mt-2 w-full min-h-24"
                  :disabled="isLocked"
                  placeholder="Drafting a short message to accompany the quote PDF…"
                ></textarea>
                <div
                  class="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500"
                >
                  <span>
                    <span v-if="aiMessageLoading">Drafting comment…</span>
                    <span v-else-if="aiMessageError" class="text-rose-500">{{
                      aiMessageError
                    }}</span>
                    <span v-else-if="sendMessage.trim()"
                      >Edit the comment before sending if needed.</span
                    >
                    <span v-else
                      >Add a short message to accompany the quote PDF.</span
                    >
                  </span>
                  <button
                    class="btn btn-sm flex items-center gap-2"
                    type="button"
                    @click.prevent="loadAiMessage({ force: true })"
                    :disabled="aiMessageLoading || isLocked"
                  >
                    <Icon.ArrowPathIcon
                      class="icon"
                      :class="{ 'animate-spin': aiMessageLoading }"
                    />
                    {{
                      sendMessage.trim()
                        ? "Regenerate comment"
                        : "Draft comment with AI"
                    }}
                  </button>
                </div>
              </div>
              <p class="text-xs text-slate-500" v-if="linkedTicketId">
                This will be sent to {{ recipientContactLabel }} and posted as a
                reply on ticket #{{ linkedTicketId }} with the quote PDF
                attached. Future replies on the ticket will continue to this
                recipient.
              </p>
              <p class="text-xs text-slate-500" v-else>
                Sending will create a new ticket to the selected recipient and
                attach the quote PDF.
              </p>
              <label
                class="mt-2 flex items-start gap-2 rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
              >
                <input
                  v-model="sendConsent"
                  type="checkbox"
                  class="mt-1 h-4 w-4"
                  :disabled="isLocked"
                />
                <span>
                  I confirm the quote PDF{{
                    sendMessage.trim() ? " and additional comment" : ""
                  }}
                  should be sent to the customer now.
                </span>
              </label>
              <p
                v-if="!canSendQuote"
                class="text-xs text-rose-500"
                role="alert"
              >
                {{ sendDisabledMessage }}
              </p>
            </div>
          </FormBox>
        </section>

        <div
          class="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 md:hidden"
        >
          <button
            class="btn min-w-24"
            type="button"
            :disabled="!canGoBack || saving"
            @click.prevent="prevStep"
          >
            Back
          </button>
          <span class="truncate text-center text-xs font-medium text-slate-500">
            {{ stepIndex + 1 }}/{{ steps.length }} · {{ currentStep?.label }}
          </span>
          <button
            v-if="canGoNext"
            class="btn accent min-w-24 rounded"
            type="button"
            :disabled="saving"
            @click.prevent="nextStep"
          >
            Next
          </button>
          <span v-else class="min-w-24 text-right text-xs font-semibold text-emerald-600">
            Review
          </span>
        </div>
      </div>

      <aside class="space-y-4 xl:sticky xl:top-4 h-fit">
        <FormBox v-if="isPts()" header="Route Map">
          <QuotePtsRouteMap
            :return-required="Boolean(quote.data.versions?.[0]?.pts_risk?.return_required)"
            :pickup-address="quote.data.versions?.[0]?.pts_risk?.pickup_address ?? ''"
            :dropoff-address="quote.data.versions?.[0]?.pts_risk?.dropoff_address ?? ''"
          />
        </FormBox>
        <FormBox header="Progress" class="hidden xl:block">
          <div class="space-y-4 text-sm text-slate-600 dark:text-slate-300">
            <div class="flex items-center justify-between">
              <span class="text-xs uppercase tracking-wide text-slate-500"
                >Status</span
              >
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold"
                :class="
                  quote.data?.status === 'accepted'
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
                    : quote.data?.status === 'sent'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                "
              >
                {{ quote.data?.status ?? "draft" }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs uppercase tracking-wide text-slate-500"
                >Quote ID</span
              >
              <span>#{{ quote.data?.id }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-xs uppercase tracking-wide text-slate-500"
                >Created</span
              >
              <span>{{ quote.data?.created_at ?? "recently" }}</span>
            </div>
            <div class="pt-2 border-t border-slate-200 dark:border-slate-700">
              <div class="text-xs uppercase tracking-wide text-slate-500 mb-2">
                Steps
              </div>
              <div class="flex flex-col gap-2">
                <button
                  v-for="(step, index) in steps"
                  :key="step.id"
                  type="button"
                  :class="stepButtonClasses(index)"
                  @click.prevent="goToStep(index)"
                >
                  <span class="text-sm font-medium">{{ step.label }}</span>
                  <span
                    v-if="stepIssueCount(step.id)"
                    class="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                  >{{ stepIssueCount(step.id) }}</span>
                  <span v-else class="text-xs text-slate-400">Step {{ index + 1 }}</span>
                </button>
              </div>
            </div>
          </div>
        </FormBox>
        <QuoteFilesPanel
          v-if="quote.data?.id"
          :quoteId="quote.data?.id"
          :files="quote.data?.files ?? []"
          :editable="editable"
          compact
          @update:files="updateQuoteFiles"
        />
        <FormBox v-if="isEvent()" header="Ticket Context">
          <template #nav>
            <button
              class="btn flex items-center gap-2"
              type="button"
              @click.prevent="ticketContextOpen = !ticketContextOpen"
            >
              <Icon.ChevronDownIcon
                class="icon transition-transform"
                :class="ticketContextOpen ? 'rotate-180' : ''"
              />
              <span>Tickets</span>
              <span class="text-xs text-slate-400"
                >{{ selectedTicketCount }} selected</span
              >
            </button>
          </template>
          <div v-if="ticketContextOpen" class="space-y-3">
            <div v-if="ticketsLoading" class="text-sm text-slate-500">
              Loading tickets...
            </div>
            <div v-else>
              <div v-if="ticketsError" class="mb-2 text-sm text-rose-500">
                {{ ticketsError }}
              </div>
              <div
                v-if="!sortedTickets.length"
                class="text-sm text-slate-500"
              >
                {{
                  customerId
                    ? "No tickets found for this customer."
                    : "No linked ticket available."
                }}
              </div>
              <div v-else class="space-y-2 max-h-72 overflow-y-auto pr-1">
                <div
                  v-for="ticket in sortedTickets"
                  :key="ticket.id"
                  class="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-2 hover:border-slate-400"
                >
                  <label class="flex min-w-0 flex-1 items-start gap-3">
                    <input
                      class="mt-1"
                      type="checkbox"
                      :checked="selectedTicketIds.includes(Number(ticket.id))"
                      @change="toggleTicketSelection(ticket.id)"
                    />
                    <div class="min-w-0">
                      <div
                        class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate"
                      >
                        {{ ticket.subject ?? `Ticket #${ticket.id}` }}
                      </div>
                      <div class="text-xs text-slate-500">
                        #{{ ticket.id }}
                        <span v-if="ticket.updated_at || ticket.created_at">
                          • {{ ticket.updated_at ? "Updated" : "Created" }}
                          {{ ticket.updated_at ?? ticket.created_at }}
                        </span>
                      </div>
                    </div>
                  </label>
                  <button
                    class="btn shrink-0 px-3 py-1 text-xs"
                    type="button"
                    :class="isReferenceTicket(ticket.id) ? 'accent' : ''"
                    :disabled="isReferenceTicket(ticket.id)"
                    @click.prevent="openReferenceTicket(ticket.id)"
                  >
                    {{ isReferenceTicket(ticket.id) ? "Shown" : "Open" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-xs text-slate-500">
            Select customer tickets to include in AI chat context.
          </div>
        </FormBox>
      </aside>
      <aside
        v-if="referenceTicketSummary"
        class="space-y-4 xl:sticky xl:top-4 h-fit xl:max-h-[calc(100vh-1rem)] xl:overflow-y-auto"
      >
        <div class="box !p-0">
          <div
            class="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-700 dark:bg-slate-800/95 lg:px-6"
          >
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-[0.2em] text-slate-400">
                Ticket Reference
              </p>
              <h3
                class="truncate text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ referenceTicketSummary.subject }}
              </h3>
              <p class="text-sm text-slate-500">
                This stays open while you move through the quote steps.
              </p>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <RouterLink
                :to="`/tickets/${referenceTicketSummary.id}`"
                class="btn"
              >
                Open ticket
              </RouterLink>
              <button
                class="btn"
                type="button"
                @click.prevent="closeReferenceTicket"
              >
                <Icon.XMarkIcon class="icon" /> Close
              </button>
            </div>
          </div>
          <div class="p-4 lg:p-6">
            <TicketInline
              :ticketId="referenceTicketSummary.id"
              embedded
              :showHeader="false"
            />
          </div>
        </div>
      </aside>
    </div>
    <div v-else-if="activeTab === 'ticket'" class="space-y-4">
      <TicketInline v-if="linkedTicketId" :ticketId="linkedTicketId" />
      <div v-else class="box text-sm text-slate-500">
        No ticket linked to this quote.
      </div>
    </div>

    <div v-if="isEvent() && activeTab === 'ticket'" class="hidden">
      <QuoteEventRisk
        ref="riskChatRef"
        :risk="quote.data.versions?.[0]?.event_risk ?? {}"
        :quoteId="quote.data?.id"
        :ticketId="linkedTicketId"
        :contextTicketIds="selectedTicketIds"
        :entries="quote.data.versions?.[0] ?? { dates: [] }"
        :editable="editable"
        @update:risk="updateEventRisk"
        @update:entries="updateVersion"
        @update:description="updateDescription"
        @add:address="addVenueAddressFromChat"
      />
    </div>

    <div
      v-if="previewModalOpen"
      class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-0 backdrop-blur sm:px-4 sm:py-8"
      @click.self="closePreviewModal"
    >
      <div
        class="h-full w-full max-w-6xl overflow-hidden bg-white shadow-2xl dark:bg-slate-800 sm:h-auto sm:max-h-full sm:rounded-2xl"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-6 sm:py-4"
        >
          <div>
            <h2
              class="text-xl font-semibold text-slate-900 dark:text-slate-100"
            >
              Quote PDF Preview
            </h2>
            <p class="text-sm text-slate-500 dark:text-slate-300">
              Review the quote document before sending.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <a
              v-if="previewFrameSrc"
              :href="previewFrameSrc"
              target="_blank"
              rel="noopener noreferrer"
              class="btn"
            >
              Open in new tab
            </a>
            <button
              class="btn"
              type="button"
              @click.prevent="closePreviewModal"
            >
              Close
            </button>
          </div>
        </div>
        <div class="relative bg-slate-100 dark:bg-slate-900">
          <div
            v-if="previewFrameLoading"
            class="absolute inset-0 z-10 flex items-center justify-center text-sm text-slate-500 dark:text-slate-300"
          >
            Loading preview...
          </div>
          <iframe
            v-if="previewFrameSrc"
            :src="previewFrameSrc"
            class="h-[calc(100vh-73px)] w-full border-0 sm:h-[75vh]"
            title="Quote PDF Preview"
            @load="onPreviewFrameLoad"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
  <div v-else>Loading..</div>
  <div
    v-if="toast.visible"
    class="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-xl"
    :class="toast.tone === 'error' ? 'bg-rose-600' : 'bg-emerald-600'"
  >
    <Icon.CheckCircleIcon v-if="toast.tone !== 'error'" class="h-4 w-4" />
    <Icon.XCircleIcon v-else class="h-4 w-4" />
    <span>{{ toast.message }}</span>
  </div>
</template>

<style scoped>
.recalc-active {
  animation: recalc-pulse 0.6s ease;
}

@keyframes recalc-pulse {
  0% {
    transform: translateY(0) scale(1);
    box-shadow: 0 0 0 rgba(14, 165, 233, 0);
  }
  50% {
    transform: translateY(-1px) scale(1.03);
    box-shadow: 0 0 8px rgba(14, 165, 233, 0.4);
  }
  100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 0 0 rgba(14, 165, 233, 0);
  }
}
</style>
